/**
 * THE WORLD PAGE'S HOST. The one file that knows the voyage layer is running in a browser
 * and draws it.
 *
 * -- THE THREE RULES THIS FILE KEEPS --------------------------------------------
 *
 *   1. THE RENDERER ONLY READS. `view(nowMs)` is the only session method called to build a
 *      frame, and `state()` is read for the chart's counts. Nothing here writes state: no
 *      sailTo, no returnToTrack, no import. Presence changes nothing, so a tab watched for
 *      an hour and a tab closed for an hour land on the identical frame.
 *   2. NO STEERING. The four dev controls move the CLOCK, never the boat. The boat's
 *      position is `view(nowMs).fix.position`, a function of paid time.
 *   3. NO PER-FRAME WORK AT IDLE. There is no render loop and no animation-frame callback.
 *      The host asks for the frame, reduces it to a key, draws only if the key changed, and
 *      sleeps until the earliest moment the key CAN change.
 *
 * -- ROUND 3, AND THE TWO OWNER NOTES IT ANSWERS ---------------------------------
 *
 * THE BOAT TELEPORTED (CONTRACT 1.5g note 5). It does not any more. `smoothAt` draws between
 * the two states either side of a 30-second window, both read out of `view`, at the real
 * sub-window fraction. src/render/interpolate.ts carries the measurement that set the window
 * and src/render/interpolate.test.ts is the fence: four draw rates from 4 Hz to 500 Hz land
 * on the identical position for the identical instant, with no tolerance.
 *
 * THERE WAS NO GUI (note 4). There is one now, and it is the chart: painted on the 960x540
 * authoring grid by src/render/chart.ts, doubled by the same integer 2 as the sea. This file
 * owns only WHEN it appears - the three things that appear and leave - and none of that
 * timing touches the simulation.
 *
 * -- ROUND 4: THE WATER MOVES, AND THE HOST STILL SLEEPS -------------------------
 *
 * THE WATER WAS DULL AND LIFELESS (note 3). Open water is now POKOMOKO's sixteen-frame loop
 * (src/render/water.ts, CONTRACT 1.5g(a)). Its frame is a pure function of the wall-clock
 * instant - floor(nowMs / WATER_FRAME_MS) mod 16 - so it goes into the frame key like the
 * camera does and the host draws when it changes, then sleeps until the next boundary. No
 * ticker, no animation frame: about five draws a second, and nothing between them. Two dev
 * overrides, for looking rather than for play: `?wave=<ms>` sets the frame duration (the
 * spec's 190 ms is MODELLED and meant to be tuned by eye) and `?field=hash` shows the
 * per-tile phase field that was tried and judged a mosaic, against the uniform default.
 *
 * -- ROUND 5: THE SEA HAS CALM, AND THE OWNER HOLDS THE KNOBS --------------------
 *
 * THE SEA WAS DIZZYING (CONTRACT 1.5i(d)): round 4 applied the reference's pixel to the whole
 * field at full amplitude. The field now falls away from the boat and from every shoreline to
 * a calm floor (src/render/water.ts WATER_CALM), and the base is a blend the owner rules by
 * eye (1.5i(c)). Every figure is a GUESS behind a parameter: `?sea=0..1` (navy .. Eastward),
 * `?floor=`, `?radius=`, `?shore=`, `?gust=`, `?gustpx=`, `?drift=`, `?hold=`, and `?field=swell|cross`
 * for the two trials against the regular diagonal. The calm field is sampled at the instant
 * the wave frame began, never at the wake, so it stays a pure function of the wall clock.
 *
 * -- WHY THE INTERFACE'S CLOCK IS NOT THE GAME'S ---------------------------------
 *
 * Every dissolve and the ledger's lift are timed on `performance.now()`, the real wall
 * clock, and never on the dev clock. Otherwise "run x60" would make a six-second log line
 * flash past in a tenth of a second, and the interface would be reporting the simulation's
 * time rather than the reader's.
 *
 * -- WHAT IS NOT THE GAME'S ------------------------------------------------------
 *
 * The dev time controls are a harness, not the game, so they are HIDDEN BY DEFAULT and sit
 * behind one small toggle in the corner. `?dev` opens with them showing; `?bare` removes even
 * the toggle, which is what the screenshots are taken through. `M` toggles the material mask.
 *
 * -- AUDIO ROUND 1: THE GAME MAKES A SOUND --------------------------------------
 *
 * It never had before. `src/audio/` owns all of it and this file owns only the four places the
 * game touches it, all of them event handlers and none of them on the draw path:
 *
 *   - `arm()` at start-up, which constructs nothing at all. No AudioContext exists until the
 *     player touches the page, so somebody who reads the sea for an hour without clicking pays
 *     nothing and sees no error - which is the case browsers punish and most games get wrong.
 *   - `observe(state)` once per step, beside the catch mark and by the same mechanism: a diff of
 *     two looks at the save. Nothing subscribes to the simulation, because a subscription is a
 *     thing a settled save would not fire and presence changes nothing.
 *   - `sheet(up)` when the ledger moves, so the one gesture on screen with no voice has one.
 *   - `mark()` when the player uses the contract's one verb.
 *
 * THE SOUND IS SILENT WHILE THE DEV CLOCK RUNS. At x60 the simulation lands a fish every few
 * frames and the moments would be a machine gun; the harness is not the game, so `observe` is
 * skipped and the snapshot is dropped, which means the return to x1 emits nothing for the hour
 * that went past. `S` quiets it, the choice persists, and there is no drawn control anywhere -
 * see the note at the foot of src/audio/index.ts for why not.
 */

import { browserSaveStorage, openPlayerVoyage, previewStorage, startPlayerVoyage, startVoyage, type RunningVoyage } from './voyage-entry';
import { mountGameUi, type GameUi } from './game-ui';
import { buildCommands, type HostSeams } from './host-commands';
import { currentBoatFittings } from './outfitter';
import { sheetOpen } from './shell';
import type { PlayerSession } from './player-session';
import { buildPresentation } from './presentation';
import { catchModelAt, revealsCatch } from './catch-presentation';
import { catchPhaseMs, createCatchCeremony, reelKey } from './catch-instrument';
import { actionForKey, type GlyphFamily } from './actions';
import { adjustFocusedValue, mountPadInput, moveFocus, type PadAction } from './input-controller';
import { offlineReturn, returnSnapshot, type OfflineReturnView, type ReturnSnapshot } from './offline-return';
import { advanceTutorial, replayTutorial } from './tutorial';
import { profileFromEnvelope } from '../profile/schema';
import { RECAST_MS, fishingVisualAt } from '../render/fishing';
import { AUDIO_LINES, bandLabels, captureOffline, createAudioEngine, measureJoinAt, measureSignal } from '../audio/index';
import {
  CAST_PAD,
  INK,
  PLATE_BOX,
  STRIP_H,
  WASH,
  chartLayout,
  paintCatchMark,
  paintChart,
  panelHeight,
  toLayers,
  toRgba,
  type ChartCopy,
  type ChartLayout,
  type Leaf,
} from '../render/chart';
import { asideFor, holdLineFor, phraseFor } from '../render/chart-copy';
import { loadPlates, platesReady } from '../render/chart-plates';
import type { Bitmap } from '../render/glyphs';
import {
  bookLine,
  catchLine,
  chartSpeech,
  hoursFor,
  hoursKey,
  hudCopy,
  makeReading,
  paintHours,
  type HoursRows,
  type LedgerHint,
} from '../hud/index';
import {
  SWALLOWED_KEYS,
  choose,
  hitAt,
  keyIntent,
  pageFor,
  type ChartIntent,
  type LedgerPage,
} from '../render/chart-control';
import { SMOOTH_WINDOW_MS, lookBudgetFor, lookShiftPx, lookWeight, makeSmoother, panBudgetFor, panOffsetPx, type PanBox, type Smoothed } from '../render/interpolate';
import { SAVE_KEYS, type SimState } from '../sim/index';
import {
  assignPlaceSprites,
  fieldIslets,
  fieldSpriteIds,
  resolveSceneryCatalogue,
  harbourSpriteIds,
  placeIsletAnchorLeagues,
  placeIsletOffsetLeagues,
  placeIslandBinding,
  placeIslets,
  spriteForPlace,
  type Islet,
  type IsletSprite,
  type PlaceIsletSources,
} from '../render/islets';
import { rotatedSide } from '../render/rotate';
// WHICH ISLAND WAS DRAWN FOR WHICH PLACE, and which area each island belongs to. Baked by
// tools/world-render/bind-places.mjs; see the `named` note in islets.ts for why it is not `authored`.
import placeIslandsBaked from '../render/baked/place-islands.json';
// READ ONLY, and it is the shape SURF-R4-F1 asked the voyage lane to publish on `VoyageFix`.
// Nothing in this file writes the voyage layer; `islets.ts` reads it the same way.
import { callAt, callHoldWeight, type PlaceCall } from '../voyage/index';
import { ART_H, ART_W, FRAME_LEAGUES, PX_PER_LEAGUE_ACROSS, PX_PER_LEAGUE_UP, leaguesToWorldPx, type Px } from '../render/scale';
import { MIN_VIEW_H, MIN_VIEW_W, NO_INSETS, fitFill, parseViewParam, type ArtView, type FillBox, type FillFit, type SafeInsets } from '../render/fill';
import { CREST_LIFT_SCALE_MAX, SEA_BLEND_DEFAULT } from '../render/palette';
import { CREST_STROKE_MAX_PX } from '../render/sea';
import { WATER_CALM, WATER_FIELD, WATER_FRAME_MS, WATER_SEA, msUntilNextWaterFrame, waterFrameAt, waterModeOf, waterTickAt, type WaterCalm, type WaterField, type WaterMode, type WaterSea } from '../render/water';
import { createWorldView, type DrawFrame, type PlacedIslet, type WorldView } from '../render/world-view';

/** The empty hours a shut sheet hands the key, allocated once so a closed chart never
 *  makes garbage for a measure it is not drawing. */
const EMPTY_HOURS: HoursRows = [];

const SAVE_KEY = 'reelin.world-dev.save.v1';
const CLOCK_KEY = 'reelin.world-dev.clock-offset-ms';
/**
 * THE TITLE'S SEA. A plain page boots to the title over a preview voyage: a fresh run on this
 * fixed seed, in storage that forgets, ticked like any other so the water and the boat move,
 * and never written to the browser. The player's own voyage opens only through Continue
 * Voyage or Set Sail. 4242 is the seed the moorings instrument scans; nothing depends on it.
 */
const PREVIEW_SEED = 4242;
const RUN_RATE = 60;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
/** The host never wakes more often than this, however fast the clock runs. */
const MIN_STEP_MS = 8;
/** And never less often than this: a still boat at night costs one look per second. */
const IDLE_POLL_MS = 1000;
/** While something on the chart is dissolving or lifting, the host wakes this often. */
const ANIMATING_STEP_MS = 16;
/** Leagues beyond the frame edge to ask for islets, so a sprite's near edge is never popped in. */
const ISLET_MARGIN_LEAGUES = { x: 6, y: 8 };

// ---- how long the three transient things last, in REAL milliseconds ------------------------
/** The hold's log line holds this long, then dissolves. Long enough to read twice. */
const HOLD_LINE_MS = 7000;
const DISSOLVE_MS = 900;
/** An answer to something the player just pressed. Shorter than the hold's line: it is a reply. */
const SPOKEN_MS = 4200;
/** The catch mark on the water. Brief: it is a note that something happened, not a readout. */
const CATCH_HOLD_MS = 1400;
const CATCH_DISSOLVE_MS = 600;
/** The chart lifting. Short, and the only easing in the interface. */
const LIFT_MS = 260;
/** More landings than this between two looks means the save settled offline, not that you
 *  watched a fish come up. No mark for those: the mark is a witness, not a receipt. */
const CATCH_BURST_MAX = 4;

// ---- the dev clock ------------------------------------------------------------------------
/**
 * THE DEV CLOCK IS THE HARNESS'S AND NEVER THE PLAYER'S. `?dev` is the one load that reads
 * the persisted offset and writes it back on pagehide; a plain load runs the player's real
 * save on the real clock, starts at offset zero, and never touches `CLOCK_KEY`. Before this
 * (fix round 1) a `+1 day` under ?dev followed by a plain load advanced the real save a day,
 * and every plain load wrote the dev key on the way out. The offset still moves in memory
 * on a plain load if a script asks (`reelinWorld.jump`, the dev strip after its toggle),
 * because those are proof surfaces; it just never survives the tab.
 */
const DEV_CLOCK = ((): boolean => {
  try { return new URLSearchParams(window.location.search).has('dev'); } catch { return false; }
})();
let offsetMs = 0;
if (DEV_CLOCK) {
  try {
    offsetMs = Number(window.localStorage.getItem(CLOCK_KEY) ?? 0) || 0;
  } catch {
    offsetMs = 0;
  }
}
let rate = 1;
let rateSinceReal = 0;
let frozenAt: number | null = null;

function persistOffset(): void {
  if (!DEV_CLOCK) return;
  try {
    window.localStorage.setItem(CLOCK_KEY, String(Math.round(offsetMs)));
  } catch {
    /* a locked-down browser; the offset lives for this tab only */
  }
}

/** The one clock everything on this page reads. */
function now(): number {
  if (frozenAt !== null) return frozenAt;
  const real = Date.now();
  const extra = rate > 1 ? (real - rateSinceReal) * (rate - 1) : 0;
  return real + offsetMs + extra;
}

function setRate(next: number): void {
  const real = Date.now();
  if (rate > 1) offsetMs += (real - rateSinceReal) * (rate - 1);
  rate = next;
  rateSinceReal = real;
  persistOffset();
}

function jump(ms: number): void {
  offsetMs += ms;
  persistOffset();
}

// ---- the page -------------------------------------------------------------------------------
// What the paper shell may ask of this page is `buildCommands` over `HostSeams`
// (host-commands.ts), built by `main` once it has a session and by `reportFatal` with no world
// at all. It lives in its own file so a test can drive the real adapter on an injected clock.

/** The mounted shell, kept at module level so a failure anywhere can reach it. */
let ui: GameUi | null = null;

/**
 * A STARTUP, IMPORT OR RENDER FAILURE RENDERS THE RECOVERY SHEET. It used to write a raw
 * `<pre>` over the sea - a stack trace where the game was, with the save unreachable behind
 * it. Now the save sheet comes up with one sentence in the record red saying what happened,
 * and Export, Import, Restore and Reload all still work, because none of them needs the
 * world. If the shell itself never mounted (the failure was before `main` got that far, or
 * inside it) a bare one is mounted over a session opened for the purpose.
 */
function reportFatal(e: unknown): void {
  console.error(e);
  const msg = e instanceof Error ? e.message : String(e);
  if (ui === null) {
    const root = document.getElementById('game-ui');
    if (root === null) return;
    try {
      const player = openPlayerVoyage({ clock: now });
      ui = mountGameUi(root, buildCommands({
        dev: false, saveKeys: SAVE_KEYS, now, storage: browserSaveStorage, player: () => player, voyageRunning: () => false,
        startReal: () => undefined, stopReal: () => undefined, freshRun: () => undefined, applySettings: () => undefined,
        presentation: () => null, ledgerOpen: () => false, lastInput: () => 'keyboard', reel: () => false,
        reload: () => { window.location.reload(); },
      }));
    } catch (inner) {
      console.error(inner);
      root.textContent = `The world failed to start: ${msg}`;
      return;
    }
  }
  ui.showFatal(`The world failed to start: ${msg}`);
}

const $ = (id: string): HTMLElement => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`world.html has no #${id}`);
  return el;
};

/** 0 at t = 0, 1 at t = 1, with the ease-out a sheet of paper actually has. */
function easeOut(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return 1 - (1 - x) * (1 - x);
}

/** 1 while held, falling to 0 across the dissolve, 0 after. Real milliseconds throughout. */
function transientInk(since: number, holdMs: number, dissolveMs: number): number {
  if (since < holdMs) return 1;
  const t = (since - holdMs) / dissolveMs;
  return t >= 1 ? 0 : 1 - t;
}

async function main(): Promise<void> {
  const params = new URLSearchParams(window.location.search);
  // ?bare is the DEFAULT look now: the game, and nothing else. The dev controls are a
  // harness and live behind one toggle; ?dev opens with them showing, ?bare removes the
  // toggle too, which is how the screenshots are taken.
  const dev = params.has('dev');
  if (dev) document.body.classList.add('dev');
  if (params.has('bare')) document.body.classList.add('clean');

  const host = $('sea');
  /**
   * HOW BIG IS THE BOX THE GAME WAS ACTUALLY GIVEN?
   *
   * This used to ask `window.screen`, which describes the MONITOR. A window is not a monitor.
   * MEASURED on the built artefact at a63ec92, on a 2560-wide display: a 960x540 window was
   * handed a 1920x1080 canvas, 25.0% of it inside the window, 975 px hanging off the right
   * edge, and `overflow: hidden` on the host so there was no scrollbar to the rest of it. No
   * boat. No chart. Nothing to suggest either existed.
   *
   * `host.clientWidth/clientHeight` is the content box of #sea, which world.html now gives a
   * definite height - the viewport less the dev strip - so the figure is free space and not a
   * restatement of what is already drawn in it. It is read here, on a resize, and on a
   * device-pixel-ratio change, and at NO OTHER TIME: a layout read per frame would put a
   * forced reflow inside the draw loop, and this lane is measured on idle cost.
   */
  /**
   * `?view=960x540` PINS THE VIEW (CONTRACT 1.5ac): the frame is the authored 960x540 banded in the
   * box exactly as it was before the game filled the screen. Every instrument that measures art or
   * frame keys loads the page this way, so no earlier bar changes what it measures. Without it the
   * frame FILLS the box at the largest whole grain that still shows enough world (render/fill.ts).
   */
  const pinnedView: ArtView | null = parseViewParam(params.get('view'));
  /**
   * THE SAFE AREA, read off the stylesheet rather than guessed. world.html gives `#safe-probe` a
   * padding of `env(safe-area-inset-*)` (which needs `viewport-fit=cover` in the viewport meta to
   * be anything but 0), so the computed padding IS the inset the operating system reports. Read
   * with the box, at the same three moments, and never in the draw loop.
   */
  const safeProbe = document.getElementById('safe-probe');
  const readInsets = (): SafeInsets => {
    if (safeProbe === null) return NO_INSETS;
    const cs = getComputedStyle(safeProbe);
    return { top: parseFloat(cs.paddingTop) || 0, right: parseFloat(cs.paddingRight) || 0, bottom: parseFloat(cs.paddingBottom) || 0, left: parseFloat(cs.paddingLeft) || 0 };
  };
  const measureBox = (): FillBox => ({
    cssW: host.clientWidth,
    cssH: host.clientHeight,
    dpr: window.devicePixelRatio || 1,
    insets: readInsets(),
    pin: pinnedView,
  });
  let fit: FillFit = fitFill(measureBox());
  /** A drawable view even while the box is under the floor, so the renderer can be built at all. */
  const drawableView = (f: FillFit): ArtView => ({ w: Math.max(1, f.view.w), h: Math.max(1, f.view.h) });
  // The open water's two dev overrides. The constants in water.ts are the rulings; these exist
  // so the owner can look at another rate or the unbroken tiling without a rebuild.
  const waveMs = Math.max(1, Math.round(Number(params.get('wave')) || WATER_FRAME_MS));
  const fieldParam = params.get('field');
  const field: WaterField = fieldParam === 'uniform' || fieldParam === 'hash' || fieldParam === 'swell' || fieldParam === 'cross' ? fieldParam : WATER_FIELD;
  // ROUND 5 (CONTRACT 1.5i(c), (d)): the base blend and the calm are the owner's to tune by eye,
  // so every one is a query parameter over a GUESSED default. `?sea=0` is the navy round 4
  // shipped on, `?sea=1` Eastward's blue. `?floor=1&gust=0` is round 4's field exactly.
  const num = (name: string, dflt: number): number => {
    const v = params.get(name);
    if (v === null || v === '') return dflt;
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
  };
  const sea = Math.min(1, Math.max(0, num('sea', SEA_BLEND_DEFAULT)));
  const calm: WaterCalm = {
    floor: Math.min(1, Math.max(0, num('floor', WATER_CALM.floor))),
    radius: Math.max(1, num('radius', WATER_CALM.radius)),
    shoreRadius: Math.max(1, num('shore', WATER_CALM.shoreRadius)),
    gust: Math.min(1, Math.max(0, num('gust', WATER_CALM.gust))),
    gustPx: Math.max(16, num('gustpx', WATER_CALM.gustPx)),
    gustDrift: Math.max(0, num('drift', WATER_CALM.gustDrift)),
    hold: Math.min(1, Math.max(0, num('hold', WATER_CALM.hold))),
  };
  // ROUND 9 (CONTRACT 1.5w): `?water=render` is the tone sea, `?water=settled` is the sea this
  // game shipped on, one reload apart from one build. Every constant of the render arm is a
  // GUESS and every one has a parameter here, the same deal WATER_CALM got in round 5.
  const waterMode: WaterMode = waterModeOf(params.get('water'));
  const seaParams: WaterSea = {
    tones: Math.max(2, Math.min(32, Math.round(num('tones', WATER_SEA.tones)))),
    shelfPx: Math.max(1, num('shelf', WATER_SEA.shelfPx)),
    deep: Math.min(1, Math.max(0, num('deep', WATER_SEA.deep))),
    mottle: Math.min(1, Math.max(0, num('mottle', WATER_SEA.mottle))),
    mottlePx: Math.max(8, num('mottlepx', WATER_SEA.mottlePx)),
    warpPx: Math.max(8, num('warppx', WATER_SEA.warpPx)),
    warpAmp: Math.max(0, num('warp', WATER_SEA.warpAmp)),
    crestGain: Math.max(0, num('crest', WATER_SEA.crestGain)),
    crestPx: Math.max(8, num('crestpx', WATER_SEA.crestPx)),
    // CONTRACT 1.5y: the four knobs of the crest that answers the land. `?crestopen=1` is the
    // sea this round inherited on the DENSITY half - one coverage everywhere - and
    // `?crestband=0` is it on the DIRECTION half, so the owner can see either half alone.
    crestShorePx: Math.max(1, num('crestshore', WATER_SEA.crestShorePx)),
    crestOpen: Math.min(1, Math.max(0, num('crestopen', WATER_SEA.crestOpen))),
    crestBandPx: Math.max(1, num('crestbandpx', WATER_SEA.crestBandPx)),
    crestBandWeight: Math.min(1, Math.max(0, num('crestband', WATER_SEA.crestBandWeight))),
    crestDashOpen: Math.min(1, Math.max(0, num('crestdashopen', WATER_SEA.crestDashOpen))),
    // THE CREST'S LIFT AND ITS PEN, the two the round that built this sea disclosed as its
    // remaining measured distance from the renders and did NOT expose. Both default to the
    // shipped number, so a plain `?water=render` is byte-identical to what came before them.
    // Clamped where the constraint lives: the lift past CREST_LIFT_SCALE_MAX can only clamp
    // inside `crestFor`, and the pen past CREST_STROKE_MAX_PX floods the crest cell solid.
    crestLift: Math.max(0, Math.min(CREST_LIFT_SCALE_MAX, num('crestlift', WATER_SEA.crestLift))),
    crestStrokePx: Math.max(0, Math.min(CREST_STROKE_MAX_PX, num('creststroke', WATER_SEA.crestStrokePx))),
    foamPx: Math.max(0, num('foam', WATER_SEA.foamPx)),
  };
  // The renderer always gets a whole number at least 1. When the box is under the floor there
  // is nothing legal to draw at, so it is built at 1 and immediately hidden by `applyFit`; the
  // window can grow at any moment and a view that exists is a view that can appear instantly.
  const view: WorldView = await createWorldView({ host, grain: Math.max(1, fit.grain), view: drawableView(fit), waterField: field, sea, calm, waterMode, seaParams });
  /**
   * THE ART VIEWPORT THE CAMERA IS BUDGETED FOR (CONTRACT 1.5ac). Everything below that used to read
   * ART_W / ART_H as "the frame" reads this instead, and `setCameraView` recomputes what depends on
   * it when a resize changes it. At 960x540 every figure is the one this file computed before.
   */
  let artView: ArtView = drawableView(fit);
  let halfW = Math.floor(artView.w / 2);
  let halfH = Math.floor(artView.h / 2);
  /** CSS px from the canvas's top-left to the UI root's: what a UI-relative position adds to be canvas-relative. */
  const uiFromCanvas = { x: 0, y: 0 };
  let uiBox = { left: 0, top: 0, w: 0, h: 0 };

  // THE SOUND. Armed here and constructed on the player's first gesture, never before it.
  const audio = createAudioEngine();

  /**
   * TWO WAYS IN. `?dev` is the harness: the voyage on the dev key starts at once, exactly as
   * every browser instrument expects. A plain load is the game: the player's session is
   * OPENED (read, not written - see `openPlayerVoyage`) so Settings and Save and Recovery
   * work from the title, and the picture under the title is the preview, which is a fresh run
   * in storage that forgets. `running` is what the renderer reads; it is swapped, never
   * mutated, and `freshRun` rebases everything that cached the one before.
   */
  const player: PlayerSession = openPlayerVoyage(dev ? { key: SAVE_KEY, clock: now } : { clock: now });
  const preview = (): RunningVoyage => startVoyage({ storage: previewStorage(), seed: PREVIEW_SEED, clock: now });
  let voyageRunning = dev;
  let running: RunningVoyage = dev ? startPlayerVoyage(player, { clock: now }) : preview();
  let returnReceipt: OfflineReturnView | null = null;
  let hiddenSnapshot: ReturnSnapshot | null = null;
  let lastUiRefreshMs = 0;
  let tutorialCatches = Object.values(player.voyage().state().catches).reduce((sum, count) => sum + count, 0);
  let tutorialArrival: string | null = null;
  let lastInput: GlyphFamily = 'keyboard';
  window.addEventListener('gamepadconnected', () => { lastInput = 'gamepad'; });
  window.addEventListener('keydown', () => { lastInput = 'keyboard'; }, { capture: true });
  // Explicit retained scenery. Unused harbour sprites and rejected shapes do not enter.
  const fieldSprites = fieldSpriteIds(view.manifest.islets);

  /**
   * EVERY SPRITE A PLACE COULD BE, with the geometry islets.ts needs to sit it on the water: the
   * generated harbour islets, and the islands a person drew. The authored ones carry their own
   * anchor, so nothing here derives where Murrin Roost sits - the drawing decides.
   */
  const placeSprites = new Map<string, IsletSprite>();
  for (const s of view.manifest.islets) {
    placeSprites.set(s.id, { id: s.id, w: s.spritePx[0], h: s.spritePx[1], anchor: { x: s.anchor[0], y: s.anchor[1] }, authored: false });
  }
  for (const s of view.authored.islands) {
    placeSprites.set(s.id, { id: s.id, w: s.spritePx[0], h: s.spritePx[1], anchor: { x: s.anchor[0], y: s.anchor[1] }, authored: true });
  }
  const isletPool = {
    sprites: placeSprites,
    harbour: harbourSpriteIds(view.manifest.islets),
    authored: new Map(view.authored.islands.map((s) => [s.place, s.id])),
    ...placeIslandBinding(placeIslandsBaked),
  };
  /**
   * THE DEAL IS MADE ONCE, OVER THE WHOLE CHART, AND HELD. Not per frame and not per visible
   * place: the frame builder culls the place list to what is on screen, and an assignment that
   * saw only the culled list would hand a place a different island as the camera moved. Dealing
   * over `chart.places` - which is a module constant, the same list for every save - is what
   * makes the answer survive a reload, a resize and a camera move alike.
   */
  const isletSources: PlaceIsletSources = {
    ...isletPool,
    assignment: assignPlaceSprites(running.session.world().chart.places, isletPool),
  };
  // Resolve geography, art reservation and body clearance once over the entire world.
  const fieldCatalogue = resolveSceneryCatalogue(running.session.world().chart.places, isletSources, fieldSprites);

  /**
   * HOW FAR OFF THE FRAME A PLACE CAN STILL BE ON SCREEN. The margin was 4 leagues, which was
   * generous for a 203 px islet and is nine times too small for a 2,483 px island: culling a
   * place by its POINT drops a 62-league island the moment its centre leaves the widened frame,
   * while three quarters of it is still in view. MEASURED off the loaded sprites, so an island
   * added later widens this by being added.
   */
  const placeCullMargin = { x: 0, y: 0 };
  for (const s of placeSprites.values()) {
    const off = placeIsletOffsetLeagues(s);
    placeCullMargin.x = Math.max(placeCullMargin.x, s.w / 2 / PX_PER_LEAGUE_ACROSS);
    placeCullMargin.y = Math.max(
      placeCullMargin.y,
      Math.abs(off + s.anchor.y / PX_PER_LEAGUE_UP),
      Math.abs(off - (s.h - s.anchor.y) / PX_PER_LEAGUE_UP),
    );
  }

  /**
   * ============================================================================================
   * THE FRAME ON ARRIVAL
   * ============================================================================================
   *
   * The camera used to BE the boat, and the arithmetic of that is in `interpolate.ts` under the
   * same heading: 270 art px of frame above the hull against a 3-league berth clearance and a
   * body 238 px tall, so seven of twenty places cut their island off at a frame edge and 63.9%
   * of Murrin Roost had never been drawn. CONTRACT 1.5n(a) says arrival is a state and that on
   * it "the island fills the frame"; this is that sentence in pixels.
   *
   * THE ROOM, and every figure is measured off something rather than chosen:
   *
   *   the band the island is fitted into   the frame less the chart's own shut strip, STRIP_H
   *   the boat's half-extent               `rotatedSide` of the BAKED hull, so a bigger boat
   *                                        narrows this by being baked, not by being retyped
   *   the margin                           the one judgement here, and it is a gap, not a rule
   *
   * WHAT THE BAND COSTS. Fitting to the shut strip rather than to the whole frame gives up 64 px
   * of downward room. It keeps the hull clear of the paper at a mooring. Murrin's measured body
   * remains taller than the band, so the fit shares its crop within the unchanged hull limits.
   * The lifted sheet is deliberately NOT tracked - the
   * camera would then move on the sheet's own animation, changing the frame key sixty times a
   * second while the player is reading, and the previous round measured the whole sheet as worth
   * one island and 0.3 points.
   */
  const boatHalfPx = rotatedSide(view.manifest.boat.spritePx[0], view.manifest.boat.spritePx[1]) / 2;
  let lookBudget = lookBudgetFor(artView.h, STRIP_H, boatHalfPx, undefined, halfH);
  /**
   * HOW MUCH OF THE ARRIVAL FRAME THE OWNER WANTS, 0 to 1, over the game's own arrival ramp.
   *
   * THERE IS NO RADIUS AND NO FALLOFF HERE, and the first version of this camera had both. It
   * took its weight from the drawn distance to the mooring over an 8-league radius, a figure
   * chosen off a sweep in `tools/lanes/surfaces/arrival.mjs`. A SCREENSHOT KILLED IT. Frozen at
   * Cloven Rigg with the chart reading "At Cloven Rigg, made fast", the drawn boat was 6.73
   * leagues off the place point - the smoother chords a step function and says so - the weight
   * came out 0.067, and the frame moved 9 px of the 134 the island needed. The island was still
   * cut off in the picture while the game said the boat had arrived.
   *
   * The weight is now `callHoldWeight` and the island is the call's own `placeId`, both carried
   * through the smoother beside the position, so the frame and the hull are drawn from the same
   * two states at the same fraction. Zero in open water by construction rather than by a measured
   * threshold, one when the game says the boat is made fast, and eased over
   * `CALL_APPROACH_LEAGUES` - the run-in the boat is already easing over.
   *
   * `?look=` scales it, in the same spirit as `?sea` and `?floor` under CONTRACT 1.5i(d): a
   * figure judged by eye gets a query parameter rather than a rebuild. `?look=0` is the camera
   * this game had before round 7 - the boat, and nothing else - so the two frames are one page
   * reload apart and the owner can rule on the change itself.
   */
  const LOOK_SCALE = Math.min(1, Math.max(0, num('look', 1)));
  /**
   * The largest the camera can ever be off the boat. Derived from the budget rather than typed,
   * so a bigger hull or a taller strip narrows the swing and widens the draw window together and
   * neither can be changed without the other following.
   */
  let maxLookShiftPx = Math.max(lookBudget.boatBottom - lookBudget.restY, lookBudget.restY - lookBudget.boatTop);
  let maxLookShiftLeagues = maxLookShiftPx / PX_PER_LEAGUE_UP;

  /**
   * THE PAN, CONTRACT 1.5q(b): a too-large island is panned along, not shrunk. The arithmetic
   * and the reasons are in `interpolate.ts` under "THE PAN"; what is here is the host's three
   * facts. The budget is `lookBudgetFor` on its side - the frame's half-width less the hull's
   * rotated half-extent and the same 12 px gap - so the boat stays in frame by the rule the
   * vertical already keeps. `?pan=` scales it in the spirit of `?look=`: `?pan=0` is the camera
   * this game had at the end of round 7, and a fraction narrows the sweep. The default is 1.
   *
   * `maxPanLeagues` widens the DRAW window across, as `maxLookShiftLeagues` widens it up, and
   * for the same reason: the window is centred on the boat and the frame no longer is. The
   * field's own window is again deliberately untouched, and `tools/lanes/surfaces/pan.mjs`
   * measures rather than assumes that the pan reveals no scenery the field was not asked for.
   */
  const PAN_SCALE = Math.min(1, Math.max(0, num('pan', 1)));
  let panBudgetPx = Math.round(panBudgetFor(artView.w, boatHalfPx) * PAN_SCALE);
  let maxPanLeagues = panBudgetPx / PX_PER_LEAGUE_ACROSS;
  /**
   * The body's box across the screen with the camera on the MOORED boat, per place, computed
   * once. The moored boat is the place's own point in both kinds of mooring: a natural call is
   * made fast at `call.point`, which `callBodiesFor` takes from `p.position`, and an explicit
   * arrival is `makeFix('moored', place.position, ...)`. So the box needs no call and no order,
   * and the sweep's geometry - how far each way, which way first - is one fixed shape per place.
   */
  const panBoxCache = new Map<string, PanBox | null>();
  function panBoxFor(placeId: string): PanBox | null {
    const hit = panBoxCache.get(placeId);
    if (hit !== undefined) return hit;
    let box: PanBox | null = null;
    const place = placeById.get(placeId);
    if (place !== undefined) {
      const spriteId = spriteForPlace(place, isletSources);
      const sprite = spriteId === undefined ? undefined : isletSources.sprites.get(spriteId);
      if (sprite !== undefined) {
        const wp = leaguesToWorldPx(place.position);
        const at = leaguesToWorldPx(placeIsletAnchorLeagues(place, sprite));
        const x0 = at.x - sprite.anchor.x - wp.x + halfW;
        box = { x0, x1: x0 + sprite.w };
      }
    }
    panBoxCache.set(placeId, box);
    return box;
  }
  /**
   * "Made fast" with `callHoldWeight`'s OWN edges, not a paraphrase of them. MEASURED on the
   * first browser run of the previous port: the save begins at Murrin Roost with
   * `enter === holdFrom === 0`, and at the epoch itself `callHoldWeight` returns 0 by its
   * `trackDistance <= enter` edge while `trackDistance >= holdFrom` said crossed. The pan's
   * clock then started one window early, inside the chord that ramps the first frame from 0 to
   * 1, and the anchor dwell was spent under it: hold 0.27, 0.59, 0.91 at the first three phases
   * photographed.
   */
  const madeFast = (call: PlaceCall, d: number): boolean => d > call.enterLeagues && d >= call.holdFromLeagues;
  /**
   * WHEN THE TRACK CROSSED A CALL'S HOLD, in save-clock ms, by bisection over
   * `view(t).fix.trackDistance` - monotone in `t` because progress is; ~27 reads per mooring, and
   * the smoother asks twice a window. Cached on the place AND the progression curve's identity
   * (seed, skill, target species - the three `session.refresh` treats as the curve), because a
   * new target re-solves the curve and moves every crossing, and the shell's own `setTarget`
   * does not call `rebuildSmoother` itself - it reaches it only at the next read through
   * `smoothedAt`, which sees the curve move. Cleared there too; the key is the belt to that brace.
   */
  const crossingCache = new Map<string, number>();
  function holdCrossingMs(call: PlaceCall, t: number): number {
    const s = running.session.state();
    const key = `${call.placeId}|${call.holdFromLeagues}|${s.seed}|${s.skill}|${s.targetSpecies}`;
    const hit = crossingCache.get(key);
    if (hit !== undefined) return hit;
    const epoch = s.epochMs;
    const crossed = (at: number): boolean => madeFast(call, running.session.view(at).fix.trackDistance);
    let lo = epoch;
    let hi = Math.ceil(t);
    if (crossed(lo)) hi = lo;
    while (hi - lo > 1) {
      const mid = Math.floor((lo + hi) / 2);
      if (crossed(mid)) hi = mid;
      else lo = mid;
    }
    crossingCache.set(key, hi);
    return hi;
  }
  /**
   * WHEN A MOORING BEGAN ON THE PICTURE, derived from the save's own clock: see `Fix.moorEpochMs`
   * in `interpolate.ts` for what it is. Two kinds of mooring on this branch, and both are
   * closed-form in the save. An EXPLICIT arrival (`phase === 'moored'`) is a SailTo order carried
   * to its end, so it began at `issuedAtMs + durationMs`, which the order persists. A NATURAL
   * call began when the track crossed its hold - unless a Return order ended inside the hold, in
   * which case the boat re-joined the moored state at the return's own end and the pan's clock
   * starts there. Snapped UP to the smoother boundary where the DRAWN weight first reaches 1.
   */
  function moorEpochFor(fix: { phase: string; trackDistance: number }, t: number): number | null {
    const order = running.session.order();
    let raw: number;
    if (fix.phase === 'moored') {
      if (order.kind !== 'sail') return null;
      raw = order.issuedAtMs + order.durationMs;
    } else if (fix.phase === 'sailing' || fix.phase === 'returning') {
      return null;
    } else {
      const call = callAt(running.session.world().calls, fix.trackDistance);
      if (call === null || !madeFast(call, fix.trackDistance)) return null;
      raw = holdCrossingMs(call, t);
      if (order.kind === 'return') raw = Math.max(raw, order.issuedAtMs + order.durationMs);
    }
    const epoch = running.session.state().epochMs;
    return epoch + Math.ceil((raw - epoch) / SMOOTH_WINDOW_MS) * SMOOTH_WINDOW_MS;
  }

  /**
   * THE SHIFT ONE STATE ASKS FOR: down-screen art px, at a drawn boat position, for the place
   * that state's call names and at that state's own arrival weight.
   *
   * IT IS CONTINUOUS IN BOTH ARGUMENTS AND THAT IS THE WHOLE REQUIREMENT. An earlier version
   * culled a body whose box did not intersect the frame, on the reasoning that the camera should
   * not move for an island nobody can see. MEASURED, that cull was the worst discontinuity in the
   * whole walk: crossing the frame edge at place-01 took the shift from 0 to -153 px in a quarter
   * of a league, 5.77 times the boat's own screen travel, and it fired 33 times over the track.
   * `fitShiftPx` already answers the question the cull was asking - an island below the frame
   * asks to be pulled up, and the clamp bounds how far - and it answers it as a straight line
   * rather than a switch. There is no cull here now, and there must not be one added.
   */
  function shiftFor(wp: Px, placeId: string | null, hold: number): number {
    const weight = lookWeight(hold, LOOK_SCALE);
    if (placeId === null || weight <= 0) return 0;
    const place = placeById.get(placeId);
    if (place === undefined) return 0;
    const spriteId = spriteForPlace(place, isletSources);
    const sprite = spriteId === undefined ? undefined : isletSources.sprites.get(spriteId);
    if (sprite === undefined) return 0;
    // The body's box in SCREEN art px with the camera still on the boat: that is the frame the
    // shift is measured from, and it is what `interpolate.ts` fits into the band.
    const at = leaguesToWorldPx(placeIsletAnchorLeagues(place, sprite));
    const y0 = at.y - sprite.anchor.y - wp.y + halfH;
    return lookShiftPx({ y0, y1: y0 + sprite.h }, lookBudget, weight);
  }

  /**
   * THE PAN ONE STATE ASKS FOR at the instant `t`: art px across, positive when the camera has
   * gone right, at that state's own weight and on that state's own mooring clock. Zero for any
   * body no wider than the frame, so eighteen places draw exactly what they drew before this.
   */
  function panFor(placeId: string | null, hold: number, moorEpochMs: number | null, t: number): number {
    const weight = lookWeight(hold, LOOK_SCALE);
    if (placeId === null || weight <= 0 || moorEpochMs === null || panBudgetPx <= 0) return 0;
    const box = panBoxFor(placeId);
    if (box === null) return 0;
    return panOffsetPx(box, artView.w, panBudgetPx, weight, t, moorEpochMs, waveMs);
  }

  /**
   * THE VIEW CHANGED SIZE (a resize, a rotation, a split view): re-derive every camera figure that is
   * a function of it. Same derivations as above, so a view of 960x540 gives the same numbers. The pan
   * box cache holds `halfW` inside it, so it is dropped; the frame key is cleared so the next step
   * draws the new framing even if the camera did not move.
   */
  function setCameraView(next: ArtView): void {
    if (next.w === artView.w && next.h === artView.h) return;
    artView = next;
    halfW = Math.floor(artView.w / 2);
    halfH = Math.floor(artView.h / 2);
    lookBudget = lookBudgetFor(artView.h, STRIP_H, boatHalfPx, undefined, halfH);
    maxLookShiftPx = Math.max(lookBudget.boatBottom - lookBudget.restY, lookBudget.restY - lookBudget.boatTop);
    maxLookShiftLeagues = maxLookShiftPx / PX_PER_LEAGUE_UP;
    panBudgetPx = Math.round(panBudgetFor(artView.w, boatHalfPx) * PAN_SCALE);
    maxPanLeagues = panBudgetPx / PX_PER_LEAGUE_ACROSS;
    panBoxCache.clear();
    lastKey = '';
    lastChartKey = '';
  }

  /**
   * THE CAMERA AT A DRAWN POSITION, chorded between the two states the smoother read.
   *
   * WHY BOTH ENDPOINTS AND NOT THE BLENDED WEIGHT. A window can span two calls, and then the
   * blended weight has lost which island it belongs to. Working out a shift for each end and
   * chording THOSE is the same operation `smoothAt` performs on the position one level down, so
   * the frame and the hull move together across a call boundary instead of the frame snapping
   * between two islands as the nearer one changes. The instrument measured that snap before this
   * shape existed: 123 px in a quarter of a league, standing out of place-15.
   *
   * It is a pure function of its argument, which is what lets `nextDelayMs` ask what the CAMERA
   * does over the window rather than what the boat does. Those were the same question until this
   * existed and the sleep budget was written when they were.
   */
  function cameraAt(g: Smoothed, t: number): { boatPx: Px; camPx: Px; shiftPx: number; panPx: number } {
    const wp = leaguesToWorldPx(g.position);
    const boatPx = { x: Math.round(wp.x), y: Math.round(wp.y) };
    const a = shiftFor(wp, g.fromPlace, g.fromHold);
    const b = shiftFor(wp, g.toPlace, g.toHold);
    const shiftPx = Math.round(a + (b - a) * g.f);
    // THE PAN, CHORDED THE SAME WAY between the same two endpoints, so a call boundary is no
    // more a step across than it is up. Both endpoints read the pan at the SAME tick `t`; only
    // the weight, the place and the mooring clock differ between them.
    const pa = panFor(g.fromPlace, g.fromHold, g.fromMoorEpochMs, t);
    const pb = panFor(g.toPlace, g.toHold, g.toMoorEpochMs, t);
    const panPx = Math.round(pa + (pb - pa) * g.f);
    if (shiftPx === 0 && panPx === 0) return { boatPx, camPx: boatPx, shiftPx: 0, panPx: 0 };
    return { boatPx, camPx: { x: boatPx.x + panPx, y: boatPx.y - shiftPx }, shiftPx, panPx };
  }

  /** The camera at ONE of the smoother's endpoints, for the sleep budget below. */
  function cameraAtEndpoint(pos: { x: number; y: number }, placeId: string | null, hold: number, moorEpochMs: number | null, t: number): Px {
    const wp = leaguesToWorldPx(pos);
    const boatPx = { x: Math.round(wp.x), y: Math.round(wp.y) };
    return { x: boatPx.x + Math.round(panFor(placeId, hold, moorEpochMs, t)), y: boatPx.y - Math.round(shiftFor(wp, placeId, hold)) };
  }

  /** The camera follows the actual order's harbour. Progress along the natural course
   * continues during explicit travel, so its unrelated calls must not frame that boat.
   * The existing smoother blends arrival framing across the same window as position.
   * `moorEpochMs` rides beside the weight for the pan (CONTRACT 1.5q(b)); see `moorEpochFor`.
   */
  function arrivalAt(fix: { phase: string; mooredAt: string | null; trackDistance: number }, t: number): { hold: number; holdPlaceId: string | null; moorEpochMs: number | null } {
    if (fix.phase === 'moored') return { hold: 1, holdPlaceId: fix.mooredAt, moorEpochMs: moorEpochFor(fix, t) };
    if (fix.phase === 'sailing' || fix.phase === 'returning') return { hold: 0, holdPlaceId: null, moorEpochMs: null };
    const call = callAt(running.session.world().calls, fix.trackDistance);
    if (call === null) return { hold: 0, holdPlaceId: null, moorEpochMs: null };
    return { hold: callHoldWeight(call, fix.trackDistance), holdPlaceId: call.placeId, moorEpochMs: moorEpochFor(fix, t) };
  }
  /** Every charted place by id, so the camera looks one up rather than scanning for it. */
  const placeById = new Map(running.session.world().chart.places.map((p) => [p.id, p]));

  /**
   * THE SMOOTHER. Rebuilt whenever the session is, because it is anchored to that save's own
   * epoch. It reads `view` and nothing else, and `view` advances nothing.
   *
   * The arrival weight rides through it beside the position because the two have to agree: they
   * are read off the SAME two states and drawn at the same fraction, so the frame commits to an
   * island exactly as the hull it is following arrives at one. Read separately they disagreed by
   * 6.73 leagues at a mooring - the measurement is on `Fix.hold` in `interpolate.ts`.
   */
  let smoother = makeSmoother(
    (t) => {
      const f = running.session.view(t).fix;
      return { position: f.position, headingDeg: f.headingDeg, ...arrivalAt(f, t) };
    },
    running.session.state().epochMs,
  );

  /**
   * THE COURSE THE MEMO WAS READ UNDER, AND THE ONE READ OF IT THAT CHECKS.
   *
   * `rebuildSmoother` below is the rule - a verb makes the memo stale - and until this existed
   * only the dev ledger's own verbs and `freshRun` called it. The paper shell's verbs do not pass
   * through this file at all: the chart's destination row, the quarry plate and "Let her drift"
   * go `commands.sailTo` / `setTarget` / `returnToNaturalCourse` (host-commands.ts) straight to
   * the player session. MEASURED (gauntlet/rounds/wired-flake/diag-stale-order.json): a save
   * moored at place-13, a sail to place-10 chosen through the chart with the clock frozen 5 s
   * into a window. The page that had drawn before the click kept framing place-13 at hold 1 while
   * its own `mooredAt` read null, held the boat on the berth for the other 25 s, then at the
   * window's edge moved the boat 46 x 94 px and the camera 46 x 227 px in 2 ms. A cold load of
   * the same save at the same instants drew none of that and moved 0 px across the same edge.
   *
   * So the memo is checked against the course on every read rather than trusted to be told.
   * The order is compared by IDENTITY, which is exact: `openVoyageSession` hands back the one
   * object it holds and replaces it only when a verb commits or an import lands. The rest are
   * the curve's identity, the three `session.refresh` rebuilds the world on, plus the epoch the
   * smoother is anchored to. A new verb added anywhere, by any surface, is covered without
   * having to remember this file.
   */
  let readUnderOrder: unknown = running.session.order();
  let readUnderCurve = curveKey();
  function curveKey(): string {
    const s = running.session.state();
    return `${s.seed}|${s.skill}|${s.targetSpecies}|${s.epochMs}`;
  }
  function smoothedAt(t: number): Smoothed {
    if (running.session.order() !== readUnderOrder || curveKey() !== readUnderCurve) rebuildSmoother();
    return smoother(t);
  }

  function labelOf(id: string): string | null {
    if (id === '') return null;
    for (const p of running.session.world().chart.places) if (p.id === id) return p.label;
    return null;
  }

  // ---- the chart's own state: what appears and leaves, and where the hand is ---------------
  const openedAtReal = performance.now();
  let holdLine: string | null = null;
  let ledgerOpen = false;
  let ledgerChangedAtReal = -1e12;
  let lastCatch: { species: string; atReal: number } | null = null;
  let catchSnapshot: Record<string, number> | null = null;
  /**
   * THE CATCH CEREMONY (Task 5): the one look that feeds the line on the water and the plate.
   * It judges through Task 3's `detectCatch` and nothing else; `noteCatches` above it is the
   * catch MARK's own diff, older than this, and keeps saying which animal and nothing more.
   * Reel records an instant here and reaches nothing settled (host-commands.test.ts).
   */
  const ceremony = createCatchCeremony();
  // The operating system's word on motion, honoured live for the line as the stylesheet
  // honours it for the sheets (Task 4 fix round 2); the profile's checkbox adds to it.
  const reducedMotionQuery: { readonly matches: boolean } = ((): MediaQueryList | { readonly matches: boolean } => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)'); } catch { return { matches: false }; }
  })();
  let lastFishingKey = '';
  function reel(): boolean {
    if (!voyageRunning || !ceremony.reel(now(), player.profile().settings)) return false;
    // The player used the one presentational verb: a mark on the paper, and the plate at once.
    audio.mark();
    ui?.refreshCatch();
    kick();
    return true;
  }
  /**
   * WHAT THE SAVE WEIGHED AND WHAT IT HELD RECORDS OF, AS OF THE LAST LOOK.
   *
   * A single catch's weight is not a field on the state - `weightG` is the running total and
   * `records` is the per-species best - so the weight of the fish that just came aboard is the
   * DELTA, and it is only a single fish's weight when exactly one landed. `noteCatches` already
   * drops any burst bigger than four as a settle rather than a session, so the same guard
   * covers this. The records map is re-snapshotted only when something landed, because nothing
   * else can move it: a drifting boat pays no allocation for either of these.
   */
  let weightSnapshot = 0;
  let recordSnapshot: Record<string, number> = {};
  let variantSnapshot: Record<string, number> = {};
  /**
   * WHETHER THE PLAYER HAS EVER OPENED THE BOOK, which is the only thing the foot's last
   * sentence waits on. It is deliberately session-scoped rather than persisted: a returning
   * player who has forgotten the key is reminded once and then not again, and no new storage
   * key is minted for a sentence.
   */
  let ledgerEverOpened = false;
  /**
   * WHICH LEAF AND WHERE THE HAND IS, and both of them are the interface's own state rather
   * than the game's. A page turn and a cursor move change nothing a save records, which is
   * why they live here; the TARGET and the ORDER are the opposite, and both are written
   * through a session verb the moment they are chosen.
   */
  let leaf: Leaf = 'species';
  let cursor = 0;
  /** What the chart is saying just now, and when it started saying it. Real milliseconds. */
  let spoken: { text: string; atReal: number } | null = null;

  /**
   * THE PLATES, ASKED FOR THE FIRST TIME THE BOOK COMES UP AND NEVER AT BOOT.
   *
   * A plate is 482 x 128 art px of drawn animal on a page that only exists while the ledger is
   * lifted, and the overwhelming majority of a session is spent watching the sea with the book
   * shut. So the 92,549 B of baked plates are not on the boot path at all: `askForPlates` is
   * called from `toggleLedger`, once, and a player who never presses L never asks for one.
   *
   * That is not only a byte argument. `tools/lanes/platform-trust/delivered.test.ts` holds every
   * PNG on a barrier at boot and asserts that the number held is exactly the number of island
   * sprites the view names - so a plate fetched during start-up would break the fence that
   * proves the first frame does not wait for art, and it would break it by being true.
   *
   * `lastChartKey` is cleared on arrival because nothing else would repaint the sheet: the key
   * is built from the copy, the copy is what the plates change, and a hand sitting still on a
   * row moves neither. Without this the plates would land and show up whenever the player next
   * happened to move.
   */
  let plates: ReadonlyMap<string, Bitmap> = new Map();
  let platesAsked = false;
  function askForPlates(): void {
    if (platesAsked) return;
    platesAsked = true;
    void loadPlates().then((loaded) => {
      plates = loaded;
      lastChartKey = '';
      kick();
    });
  }

  function noteCatches(): void {
    const state = running.session.state();
    const catches = state.catches;
    if (catchSnapshot === null) {
      catchSnapshot = { ...catches };
      recordSnapshot = { ...state.records };
      variantSnapshot = { ...state.variants };
      weightSnapshot = state.weightG;
      return;
    }
    let total = 0;
    let best: string | null = null;
    let bestDelta = 0;
    for (const [id, n] of Object.entries(catches)) {
      const d = n - (catchSnapshot[id] ?? 0);
      if (d <= 0) continue;
      total += d;
      if (d > bestDelta) {
        bestDelta = d;
        best = id;
      }
    }
    // The first of its species, read off the OLD snapshot before it is replaced.
    const fresh = best !== null && (catchSnapshot[best] ?? 0) === 0;
    catchSnapshot = { ...catches };
    if (best === null) return;
    // ONE FISH, ONE WEIGHT. The delta is that animal's own weight only when it is the only
    // thing that landed; on a bigger burst the sheet says the animal and no figure, which is
    // exactly what it said before this existed.
    const grams = total === 1 ? Math.max(0, state.weightG - weightSnapshot) : 0;
    const beat = (state.records[best] ?? 0) > (recordSnapshot[best] ?? 0);
    // WHICH FORM CAME ABOARD. Read by diffing the variants tally, exactly as the catch itself
    // is read, so nothing new subscribes to the simulation. Only claimed when a single fish
    // landed: on a burst the sheet already declines to name a weight, and naming a form while
    // several animals arrived would attach it to the wrong one.
    let form: string | undefined;
    if (total === 1) {
      for (const [key, n] of Object.entries(state.variants)) {
        if (n - (variantSnapshot[key] ?? 0) <= 0) continue;
        const cut = key.indexOf(':');
        if (cut < 0 || key.slice(0, cut) !== best) continue;
        form = key.slice(cut + 1);
        break;
      }
    }
    recordSnapshot = { ...state.records };
    variantSnapshot = { ...state.variants };
    weightSnapshot = state.weightG;
    if (total > CATCH_BURST_MAX) return;
    lastCatch = { species: best, atReal: performance.now() };
    // THE RECORD, SAID AT THE MOMENT IT IS SET. `records` is earned and never purchasable
    // (CONTRACT 1.5l(a)); it is also the one figure a player would never notice arriving,
    // because the catch mark on the water shows which animal and nothing else.
    // THE REVEAL SETTING DECIDES WHETHER THE SHEET SAYS IT. `all` says every fish; `notable`
    // only a record or a first; `new-only` only a first. The mark on the water is not a
    // reveal and is not gated. The same rule feeds Task 5's plate through `catchPresentationAt`.
    const kind = beat && fresh ? 'new-record' : beat ? 'record' : fresh ? 'new' : 'ordinary';
    // A first fish has no earlier best to beat, so it is not "heaviest yet" (the plate agrees).
    if (rate === 1 && revealsCatch(player.profile().settings.catchReveal, kind)) say(catchLine(best, grams, beat && !fresh, form));
  }

  /**
   * WHETHER THE FOOT STILL HAS TO SAY WHERE THE LEDGER IS.
   *
   * It stops the moment the book has been opened, and it says what the book is FOR while the
   * player has never chosen a fish - which is the state a new save is in, and the state in
   * which a bare key name would be an instruction with no reason attached to it.
   */
  function hintNow(state: SimState): LedgerHint {
    // CONTRACT 1.5aa: outside ?dev nothing opens the canvas ledger, so nothing may say L does.
    // The foot reaches a player through the plain-type page even while the canvas is shut.
    if (!dev || ledgerEverOpened) return 'none';
    return state.targetSpecies === '' ? 'choose' : 'ledger';
  }

  /** The rows on show, and the ids behind them. Rebuilt per frame; it is a pure read. */
  function pageAt(t: number): LedgerPage {
    return pageFor(leaf, running.session.state(), running.session.view(t));
  }

  function chartCopyAt(t: number, real: number): ChartCopy {
    const v = running.session.view(t);
    const state = running.session.state();
    const holdInk = holdLine === null ? 0 : transientInk(real - openedAtReal, HOLD_LINE_MS, DISSOLVE_MS);
    const spokenInk = spoken === null ? 0 : transientInk(real - spoken.atReal, SPOKEN_MS, DISSOLVE_MS);
    const lifting = Math.min(1, (real - ledgerChangedAtReal) / LIFT_MS);
    const lift = ledgerOpen ? easeOut(lifting) : 1 - easeOut(lifting);
    const page = pageAt(t);
    // THE ONE LINE THE CHART SPEAKS WITH, and three things want it. What the player just did
    // wins, then the hold's disclosure, then the standing aside - most recent first, because
    // an answer to the thing you pressed a second ago is the only one you are still reading.
    //
    // SURFACES ROUND 2 adds a fourth speaker at the bottom of that order, and only
    // while the book is open: the book's own tally (src/hud/book.ts bookLine). The
    // standing aside says where the next land is, which is what you want while you
    // are watching the sea; with the ledger lifted you are reading the book, and
    // what the book knows about itself belongs on the line the sheet already speaks
    // from. It leaves with the lift, so nothing here becomes a permanent counter.
    const aside =
      spokenInk > 0 && spoken !== null
        ? spoken.text
        : holdInk > 0 && holdLine !== null
          ? holdLine
          : lift > 0
            ? bookLine(state)
            : asideFor(v, labelOf);
    // EVERYTHING THE SAVE PUTS ON THE SHEET IS ASSEMBLED BY `hudCopy`, not here. The clause
    // that says what the boat is after, the quarry's mark and count, its record, and the
    // foot's standing account all come out of one function, which is the same function the
    // earned-is-rendered fence renders through. A second assembly here is how a fence about
    // pixels goes green on pixels the player never sees.
    return hudCopy({
      state,
      phrase: phraseFor(v, labelOf),
      aside,
      asideInk: spokenInk > 0 ? spokenInk : holdInk > 0 ? holdInk : 1,
      lift,
      leaf,
      cursor: ledgerOpen ? Math.min(cursor, page.rows.length - 1) : -1,
      // THE STRIKE IS THE VOYAGE LAYER'S OWN EXIT, not this file's guess at one.
      // `helm.exit.needed` is false in exactly one state - the boat already living its
      // natural progression - and true in every other, which includes moored at the place a
      // sail order delivered it to. That is wayfinding clause W5 ("the exit is on the entry
      // surface") drawn rather than described: the sheet that took the boat off its track is
      // the sheet that puts it back, and it carries the mark for exactly as long as it needs to.
      exit: v.chart.helm.exit.needed,
      ledger: page.rows,
      hint: hintNow(state),
      // THE PLATE IS PICKED BY `hudCopy`, NOT HERE, for the reason the comment above gives
      // about the rest of the copy: a second assembly in the host is how a fence about pixels
      // goes green on pixels the player never sees. This hands it the two raw facts the choice
      // needs - the ids behind the rows, and every plate that has loaded - and no rule.
      ids: page.ids,
      plates,
    });
  }

  /**
   * Everything about the chart that could change a pixel, as one string. The host repaints
   * the 960xH buffer only when this moves, so a still chart costs nothing per step.
   */
  function chartKey(c: ChartCopy, layout: ChartLayout | null, hours: HoursRows): string {
    const ledger = c.lift > 0 ? c.ledger.map((r) => `${r.name}${r.count ?? -r.level}${r.marked ? '*' : ''}`).join(',') : '';
    // The hours are keyed at the PIXEL the rule reaches, never at the point total
    // behind it: 376 art px against MASTERY_FULL is one pixel per 191 points, so a
    // row can move this key at most once per 191 seconds of landing casts. Shut, the
    // layout is null, the key is the empty string, and mastery cannot wake the host.
    const rules = layout === null ? '' : hoursKey(c, layout, hours);
    // The foot and the record are in the key because they are drawn, and they are cheap to
    // keep there for the same reason they are cheap to draw: `countWords` rounds, so the
    // account moves once per hundred coins above ten thousand rather than once per catch.
    // THE PLATE IS IN THE KEY BECAUSE IT IS DRAWN AND IT CAN CHANGE WITHOUT ANYTHING ELSE
    // MOVING. The plates arrive from a fetch, so the first sheet after they land has the same
    // phrase, the same hand and the same rows as the one before it and 482 x 128 art px of
    // difference. Its size is enough to key it: the animal under the hand is already fixed by
    // `cursor` and `ledger` above, so this only has to say WHETHER there is a plate and how big.
    const plate = c.plate === null || c.plate === undefined ? '' : `${c.plate.w}x${c.plate.h}`;
    return `${c.phrase}|${c.aside}|${Math.round(c.asideInk * 16)}|${c.markSpecies}|${c.markCount}|${c.record}|${c.foot}|${panelHeight(c.lift)}|${c.leaf}|${c.cursor}|${c.exit}|${ledger}|${rules}|${plate}`;
  }

  /**
   * The sheet's layout and the mastery behind the rows on show, or a pair of empties.
   *
   * NOTHING IS BUILT WHILE THE SHEET IS SHUT, and that is the whole of what the hours
   * cost at idle: a boat drifting on a closed chart pays one comparison per step for
   * this and allocates nothing. Both are computed once per step and handed to the key
   * and to the painter, so an open sheet does not lay itself out twice either.
   */
  function hudAt(t: number, copy: ChartCopy): { layout: ChartLayout | null; hours: HoursRows } {
    if (copy.lift <= 0) return { layout: null, hours: EMPTY_HOURS };
    return { layout: chartLayout(copy), hours: hoursFor(pageAt(t), running.session.state()) };
  }

  function computeFrame(t: number): DrawFrame {
    const session = running.session;
    // ONE READ OF THE BOAT, INTERPOLATED. `pos` is a point between two states the smoother
    // read out of `view`; it is never a state this file made up, and it is a pure function
    // of `t`, so the draw rate cannot reach it.
    const g = smoothedAt(t);
    const pos = g.position;
    const { boatPx, camPx, shiftPx, panPx } = cameraAt(g, t);
    const heading = ((Math.round(g.headingDeg) % 360) + 360) % 360;
    // The sea's loop frame: a pure function of `t`, so presence and draw rate cannot reach it.
    const quietMotion = player.profile().settings.reducedMotion || reducedMotionQuery.matches;
    const waterFrame = quietMotion ? 0 : waterFrameAt(t, waveMs);
    const places = session.world().chart.places;
    // The window scales with the art viewport (CONTRACT 1.5ac); at 960x540 the ratio is exactly 1.
    const hw = (FRAME_LEAGUES.w * (artView.w / ART_W)) / 2 + ISLET_MARGIN_LEAGUES.x;
    const hh = (FRAME_LEAGUES.h * (artView.h / ART_H)) / 2 + ISLET_MARGIN_LEAGUES.y;
    // The arrival camera widens chart culling, up by the swing and across by the pan's budget.
    // Scenery identities and clearance were resolved against all charted places at startup, so
    // a viewport change cannot create/remove a neighbour. The field's own window below is
    // deliberately untouched by the pan: `tools/lanes/surfaces/pan.mjs` measures whether any
    // panned frame can show a scenery islet the window was not asked for, rather than assuming.
    const toDraw = places.filter(
      (p) =>
        Math.abs(p.position.x - pos.x) <= hw + placeCullMargin.x + maxPanLeagues &&
        Math.abs(p.position.y - pos.y) <= hh + placeCullMargin.y + maxLookShiftLeagues,
    );
    const islets: Islet[] = [
      ...placeIslets(toDraw, isletSources),
      ...fieldIslets(
        pos.x - hw,
        pos.y - hh,
        pos.x + hw,
        pos.y + hh,
        session.state().seed,
        fieldCatalogue.islets,
        [],
      ),
    ];
    const placed: PlacedIslet[] = islets.map((i) => {
      const px = leaguesToWorldPx(i);
      return { id: i.id, sprite: i.sprite, x: px.x, y: px.y };
    });
    return {
      // THE SHIFT IS IN THE KEY, and it has to be. The host draws only when this string changes,
      // and camera and boat are now two points: mid-swing the boat can move exactly as far as the
      // camera swings the other way, leaving `camPx` on the pixel it was on while the hull has
      // travelled across the frame. Keyed on the camera alone that frame is never drawn.
      key: `${camPx.x},${camPx.y},${shiftPx},${panPx},${heading},w${waterFrame}`,
      nowMs: t,
      camPx,
      boatPx,
      boatHeadingDeg: heading,
      waterFrame,
      waterTickMs: quietMotion ? 0 : waterTickAt(t, waveMs),
      islets: placed,
    };
  }

  /**
   * Art pixels THE CAMERA crosses per real millisecond, from the window the smoother read.
   *
   * It used to be the BOAT's travel, which was the same number while the camera was pinned to the
   * hull and is not any more: on an arrival the frame swings up to `maxLookShiftPx` while the boat
   * covers its last `LOOK_RADIUS_LEAGUES`, so the camera outruns it. The sleep below is a
   * half-art-pixel budget, so asking the boat would sleep through part of the swing and step the
   * frame. `cameraAt` is pure and costs one pass over twenty places, so the same chord over the
   * same window is simply taken on the camera instead. It is still a CHORD and still averages a
   * window, exactly as the boat's did - what changes is that it is now a chord of the thing the
   * budget is about.
   */
  function cameraPxPerRealMs(t: number): number {
    const g = smoothedAt(t);
    // Both endpoints take the pan at the same tick, so at a mooring the pan contributes nothing
    // here - it advances on the water's own boundary, which `nextDelayMs` already wakes for.
    // Across an arrival or a stand-out the two weights differ and the chord sees the pan.
    const a = cameraAtEndpoint(g.from, g.fromPlace, g.fromHold, g.fromMoorEpochMs, t);
    const b = cameraAtEndpoint(g.to, g.toPlace, g.toHold, g.toMoorEpochMs, t);
    return (Math.hypot(b.x - a.x, b.y - a.y) / Math.max(1, g.t1 - g.t0)) * rate;
  }

  // ---- the sheet's second face: the same words, in type the browser owns --------------------
  /**
   * THE PLAIN-TYPE PAGE, and why it REPLACES the picture instead of lying over it.
   *
   * CONTRACT section 6 requires readable scalable text and this game had none: every word
   * is one-bit glyphs painted into a WebGL canvas at 5 art pixels of cap height, and
   * browser zoom cannot reach them. It is not that zoom helps a little - it does nothing
   * at all. `fitToBox` picks a whole-number grain from the box measured in CSS pixels, so
   * zooming to 200% halves the CSS box, drops the grain from 2 to 1, and the type comes
   * out the same physical size on the reader's screen; past that the box falls under the
   * 960x540 floor and the game shows its refusal notice. MEASURED, 2026-09-03.
   *
   * CONTRACT 1.5e forbids a fractional grain, so the picture cannot be made to grow with a
   * text size. The resolution is the one this page already reached for the too-small
   * notice, in its own words: browser text is "only on screen when the pixel art is not."
   * The pixel art is never fractionally scaled and the scalable text is never over it,
   * because the two are never on screen at once.
   */
  let reading = false;
  /** What `main` is called while the picture is in it, kept so the pane can put it back. */
  const SEA_LABEL = host.getAttribute('aria-label') ?? 'The sea';
  const readout = makeReading({
    lines: $('say-lines'),
    aside: $('say-now'),
    heading: $('say-leaf'),
    list: $('say-rows'),
    keys: $('say-keys'),
  });
  const readBtn = $('readbtn') as HTMLButtonElement;
  const ledgerBtn = $('ledgerbtn') as HTMLButtonElement;
  const driftBtn = $('driftbtn') as HTMLButtonElement;
  const sayHead = $('say-head');
  const sayRows = $('say-rows');

  /** The canvas is on screen when there is a picture to draw and nothing is covering it. */
  function showCanvas(): void {
    // Set on the element and not in the stylesheet, because world-view.ts writes
    // `display: block` onto it and an inline style beats every rule in world.html.
    view.canvas.style.display = fit.playable && !reading ? 'block' : 'none';
  }

  /** Draw, unless the picture is not the thing on screen. Textures stay current either way. */
  function paint(frame: DrawFrame): void {
    if (!reading) view.draw(frame);
  }

  /**
   * The sheet, said, and the three controls that follow the sheet's own state.
   *
   * The ledger button carries `aria-expanded` because it opens the listbox below it, and
   * the drift button exists only while `copy.exit` does - the same rule the sheet keeps
   * for the mark it draws, so the page never offers a way out of a course the boat is not
   * on. Both are read off the ChartCopy rather than off the flags this file keeps, so
   * there is nothing here that can disagree with what was painted.
   */
  function sayIt(copy: ChartCopy): void {
    readout.render(chartSpeech(copy, { ledger: dev }), reading);
    const open = copy.lift > 0;
    // The canvas ledger is ?dev's alone (CONTRACT 1.5aa), so its button is too.
    ledgerBtn.hidden = !dev;
    ledgerBtn.setAttribute('aria-expanded', String(open));
    ledgerBtn.textContent = open ? 'Close the chart ledger' : 'Open the chart ledger';
    driftBtn.hidden = !copy.exit;
    sayRows.tabIndex = reading && open ? 0 : -1;
  }

  /**
   * Apply the plain-type page. The SHELL decides when (`ui.dispatch({ kind: 'read' })`); this
   * is the page doing what the shell decided, and it is called from the shell's listener and
   * nowhere else. `moveFocus` is false on the `?read` boot path, which must not move focus
   * during start-up.
   */
  function applyReading(on: boolean, moveFocus = true): void {
    if (on === reading) return;
    reading = on;
    document.body.classList.toggle('reading', on);
    readBtn.setAttribute('aria-pressed', String(on));
    readBtn.textContent = on ? 'Show the picture again' : 'Read the chart as plain text';
    // THE LANDMARK IS NAMED FOR WHAT IS IN IT. `main` holds the sea while the picture is
    // on and the plain-type page while it is not, and under `overflow: auto` it is also
    // the scrollable region Chrome puts in the tab order - so a reader who tabs into it
    // must be told which of the two they are standing in.
    host.setAttribute('aria-label', on ? 'The chart, in plain type' : SEA_LABEL);
    showCanvas();
    // The controls follow the sheet, and the sheet has not changed - so `chartKey` will not
    // move and `step` will not call `sayIt`. Without this the ledger's listbox keeps
    // `tabindex="-1"` after the page opens and Tab walks straight past the ledger.
    sayIt(chartCopyAt(now(), performance.now()));
    // The picture has been off; nothing about the frame key changed while it was, so it
    // has to be told to draw one. Clearing the key is how a cold start would have got here.
    if (!on) lastKey = '';
    // FOCUS FOLLOWS THE THING THAT APPEARED, and goes back to the control that summoned
    // it. Without this a reader who turns the page on is left on a button behind a page
    // they cannot see, and one who turns it off is left on a heading that no longer exists.
    if (moveFocus) (on ? sayHead : readBtn).focus();
    kick();
  }
  function setReading(on: boolean): void {
    ui?.dispatch({ kind: 'read', on });
  }

  // ---- change-driven stepping -------------------------------------------------------------
  let lastKey = '';
  let lastChartKey = '';
  let lastCatchKey = '';
  let steps = 0;
  let timeoutHandle = 0;
  const clockEl = $('clock');
  let lastClockText = '';

  function showClock(t: number, phase: string): void {
    if (!document.body.classList.contains('dev')) return;
    const d = new Date(t);
    const text = `${d.toISOString().slice(0, 10)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')} local · ${phase}${rate > 1 ? ` · running x${rate}` : ''}`;
    if (text !== lastClockText) {
      lastClockText = text;
      clockEl.textContent = text;
    }
  }

  /** Main-thread time this page spent in its own step, for the idle measurement. */
  let busyMs = 0;

  /**
   * Real milliseconds until the picture can next change, so the host sleeps exactly that long
   * and never wakes for a frame that would draw nothing. Three things move a frame now: the
   * sea's loop advancing, the camera crossing an art pixel, and the chart's own transients.
   * The sea sets the floor: about five wakes a second, each one a draw, and nothing between.
   * The pan at a mooring (CONTRACT 1.5q(b)) is deliberately NOT a fourth: it advances only on
   * the water's tick, so every FRAME-KEY change it needs is one the sea was already waking for.
   * MEASURED on this host in `gauntlet/rounds/world-render-r8/pan.json` `browser.frameKeyChanges`,
   * moored at Murrin Roost, the same span of one mooring stepped at 2 ms with `?pan=1` and
   * `?pan=0`: the same number of key changes, and no instant where the camera moved and the
   * water did not.
   *
   * THE HOST'S OWN DRAW COUNTER IS STILL HIGHER WITH THE PAN ON, and this comment will not
   * pretend otherwise: `browser.drawsPerSecond` moored at Murrin Roost. `paint` is reached from
   * five places in `stepInner`, and the catch mark, the fishing visual and the plate's placement
   * are all laid out from the BOAT'S SCREEN POSITION - `frame.boatPx - frame.camPx` - which is
   * exactly what the pan moves. A ceremony running while the sweep runs re-lays itself. That is
   * a cost of the pan; it is simply not a cost this function can schedule away, because the
   * frame it would sleep until is one the sea was waking for anyway.
   */
  function nextDelayMs(t: number, animating: boolean): number {
    if (player.profile().settings.reducedMotion || reducedMotionQuery.matches) return IDLE_POLL_MS;
    if (animating) return ANIMATING_STEP_MS;
    let d = Math.min(IDLE_POLL_MS, msUntilNextWaterFrame(t, waveMs) / rate);
    const pxPerMs = cameraPxPerRealMs(t);
    // Half an art pixel of travel: fine enough that no camera step is ever missed, coarse
    // enough that a slow boat does not wake the tab sixty times a second for nothing.
    if (pxPerMs > 0) d = Math.min(d, 0.5 / pxPerMs);
    return Math.max(MIN_STEP_MS, Math.ceil(d));
  }

  let dead = false;
  function step(): void {
    if (dead) return;
    try {
      stepInner();
    } catch (e) {
      // The loop is dead either way; the difference is whether the player is told and can
      // still reach the save. `dead` keeps a kick or a resize from running it again.
      dead = true;
      drawing = false;
      reportFatal(e);
    }
  }
  function stepInner(): void {
    const t0 = performance.now();
    timeoutHandle = 0;
    // THE BOX IS TOO SMALL, so there is no frame to draw and nothing is scheduled. Not a
    // throttle and not a pause: the notice is up, the canvas is hidden, and the loop is off
    // until `applyFit` finds a box that fits and kicks it. A hidden canvas redrawn five times
    // a second is battery spent on a picture nobody can see.
    if (!fit.playable || document.hidden) return;
    steps++;
    const t = now();
    const real = performance.now();

    // The preview under the title fishes too - it is a real run - but it is nobody's catch, so
    // it is neither said nor sounded. `freshRun` re-baselines the snapshots when a voyage starts.
    if (voyageRunning) noteCatches();
    // THE SAME LOOK, HEARD. `observe` diffs the save against the previous step exactly as
    // `noteCatches` just did, and both drop a burst bigger than four as a settle rather than a
    // session. While the dev clock runs, the snapshot is dropped instead: an hour of landings
    // compressed into a minute is bookkeeping, and bookkeeping is silent.
    if (rate === 1 && voyageRunning) audio.observe(running.session.state());
    else audio.forget();

    const frame = computeFrame(t);
    if (frame.key !== lastKey) {
      lastKey = frame.key;
      paint(frame);
    }

    // The detailed ledger is opt-in. The player shell now owns activity, progression,
    // records and course controls; the old closed strip remains only in dev instruments.
    const copy = chartCopyAt(t, real);
    const hud = hudAt(t, copy);
    const showChart = dev || ledgerOpen;
    const key = `${showChart}:${chartKey(copy, hud.layout, hud.hours)}`;
    if (key !== lastChartKey) {
      lastChartKey = key;
      const h = panelHeight(copy.lift);
      // The bitmap carries CAST_PAD rows of shadow ABOVE its own top, so the sprite sits
      // that much higher and the paper's first row still lands on ART_H - h.
      const sheet = showChart ? paintChart(copy) : null;
      // The hours, ruled under the rows the chart just placed, from the chart's own
      // published layout so there is no second copy of the ledger's geometry.
      if (sheet !== null) {
        if (hud.layout !== null) paintHours(sheet, copy, hud.layout, hud.hours);
        const layers = toLayers(sheet);
        // The canvas ledger is authored 960 wide (?dev only); on a wider view it stands centred.
        view.setChart(layers.paper, layers.cast, artView.h - h - CAST_PAD, chartX());
      } else view.setChart(null, null, 0);
      paint(frame);
      // THE SAME SHEET, SAID - and it costs nothing at idle because it is inside this
      // branch. `chartKey` is the answer to "would a pixel of the chart differ", so a
      // still chart on a drifting boat never reaches here, and when it does the page is
      // already about to repaint a 960-wide bitmap. Every write inside `render` is
      // compared first, which is what keeps a dissolving aside from re-announcing itself
      // on each of the sixteen dither levels the key moves through.
      sayIt(copy);
    }

    // The catch mark on the water: the only motion the interface has.
    const catchInk = lastCatch === null ? 0 : transientInk(real - lastCatch.atReal, CATCH_HOLD_MS, CATCH_DISSOLVE_MS);
    const catchKey = lastCatch === null ? '' : `${lastCatch.species}:${Math.round(catchInk * 16)}`;
    if (catchKey !== lastCatchKey) {
      lastCatchKey = catchKey;
      const bmp = lastCatch !== null && catchInk > 0 ? paintCatchMark(lastCatch.species, catchInk) : null;
      // Off the BOAT, not off the frame's centre: the two are the same point in open water and
      // up to `maxLookShiftPx` apart at a mooring, and a fish came aboard the boat.
      view.setCatchMark(bmp ? toRgba(bmp) : null, 46, -34);
      paint(frame);
    }
    if (catchInk === 0 && lastCatch !== null) lastCatch = null;

    // THE LINE ON THE WATER, AND THE PLATE. One model (Task 3's, with Reel applied) read once
    // per step; the water is repainted only when a pixel of the line would differ, the plate
    // only when a word of it would. The plate stands bottom-centre when it fits under the
    // stern and one column left when it does not or the arrival camera has the boat down the
    // frame: the instrument chooses once per ceremony from the boat's row in this frame and
    // the plate's own measured height (render/fishing.ts `platePlacement`, game-ui.ts
    // `placePlate`); the line runs into it during the reveal.
    let ceremonyMoving = false;
    if (voyageRunning) {
      ceremony.look({ state: running.session.state(), profile: player.profile(), view: running.session.view(t), nowMs: t });
      const settings = player.profile().settings;
      const model = catchModelAt(t, ceremony.ephemeral(), settings);
      const phaseMs = catchPhaseMs(model, ceremony.ephemeral(), t, settings);
      const boatCx = frame.boatPx.x - frame.camPx.x + halfW;
      const boatCy = frame.boatPx.y - frame.camPx.y + halfH;
      // Placement can become unsafe on arrival or text resize even if the catch words did not change.
      ui?.refreshCatch({ boatScreenY: boatCy, artPx: fit.apparent, viewW: artView.w, viewH: artView.h });
      const anchor = model.phase === 'reveal' ? (ui?.catchPlateAnchor() ?? null) : null;
      // The anchor is in the UI root's CSS px; the root sits inside the safe area and the canvas does
      // not, so the offset between their origins is added before turning CSS px into art px.
      const plateFromBoatPx = anchor === null ? null : { x: Math.round((anchor.x + uiFromCanvas.x) / fit.apparent - boatCx), y: Math.round((anchor.y + uiFromCanvas.y) / fit.apparent - boatCy) };
      const visual = fishingVisualAt({ phase: model.phase, phaseMs, waterFrame: frame.waterFrame, reducedMotion: settings.reducedMotion || reducedMotionQuery.matches, kind: model.event?.kind ?? null, plateFromBoatPx });
      const fishingKey = JSON.stringify(visual);
      if (fishingKey !== lastFishingKey) {
        lastFishingKey = fishingKey;
        view.setFishingVisual(visual);
        paint(frame);
      }
      ceremonyMoving = model.phase !== 'settled' || (model.event !== null && phaseMs < RECAST_MS);
    } else if (lastFishingKey !== '') {
      lastFishingKey = '';
      view.setFishingVisual(null);
      paint(frame);
    }

    showClock(t, running.session.view(t).fix.phase);
    if (voyageRunning && real - lastUiRefreshMs >= 1000) {
      lastUiRefreshMs = real;
      const count = Object.values(running.session.state().catches).reduce((sum, value) => sum + value, 0);
      if (count > tutorialCatches) player.setTutorial(advanceTutorial(player.profile().tutorial, { kind: 'catch', first: true }));
      tutorialCatches = count;
      const arrived = running.session.view(t).fix.mooredAt ?? null;
      if (arrived !== null && arrived !== tutorialArrival) player.setTutorial(advanceTutorial(player.profile().tutorial, { kind: 'arrival' }));
      tutorialArrival = arrived;
      ui?.refresh();
    }

    const liftMoving = real - ledgerChangedAtReal < LIFT_MS;
    const holdMoving = holdLine !== null && real - openedAtReal < HOLD_LINE_MS + DISSOLVE_MS;
    const spokenMoving = spoken !== null && real - spoken.atReal < SPOKEN_MS + DISSOLVE_MS;
    const animating = liftMoving || holdMoving || spokenMoving || catchInk > 0 || ceremonyMoving;
    if (spoken !== null && !spokenMoving) spoken = null;
    timeoutHandle = window.setTimeout(step, frozenAt !== null && !animating ? IDLE_POLL_MS : nextDelayMs(t, animating));
    busyMs += performance.now() - t0;
  }

  function kick(): void {
    if (timeoutHandle) window.clearTimeout(timeoutHandle);
    timeoutHandle = 0;
    step();
  }

  /**
   * THE CANVAS LEDGER IS THE HARNESS'S NOW (CONTRACT 1.5aa, the owner: "It should only use the
   * new GUI"). Outside `?dev` no player path may lift it; closing is always allowed. `instrument`
   * is the one exception, `reelinWorld.setLedger`, which a script calls and no control reaches.
   */
  function toggleLedger(open?: boolean, instrument = false): void {
    const next = open ?? !ledgerOpen;
    if (next === ledgerOpen) return;
    if (next && !dev && !instrument) return;
    ledgerOpen = next;
    // The foot's last sentence has done its job the first time the book comes up, whether it
    // was opened by the key it names, by a finger on the paper, or by ?ledger.
    if (next) ledgerEverOpened = true;
    if (next) askForPlates();
    ledgerChangedAtReal = performance.now();
    audio.sheet(next);
    kick();
  }

  function say(line: string | null): void {
    if (line === null) return;
    spoken = { text: line, atReal: performance.now() };
  }

  /**
   * A VERB HAS BEEN CALLED, so everything the renderer had cached about the future is stale.
   *
   * The smoother memoises the two states either side of the current 30-second window, and a
   * new target or a new order changes what `view` answers for BOTH of them - the target
   * because the progression curve is solved per species, the order because the boat is on a
   * different line. Keeping the memo would draw the old future for up to half a minute.
   * Rebuilding it is not inventing state: the next draw reads `view` again, exactly as a cold
   * start would, so the drawn position stays a pure function of the instant.
   */
  function rebuildSmoother(): void {
    crossingCache.clear();
    smoother = makeSmoother(
      (t) => {
        const f = running.session.view(t).fix;
        return { position: f.position, headingDeg: f.headingDeg, ...arrivalAt(f, t) };
      },
      running.session.state().epochMs,
    );
    readUnderOrder = running.session.order();
    readUnderCurve = curveKey();
    lastKey = '';
    lastChartKey = '';
  }

  /** The single place a pointer or a key turns into something happening. */
  function act(intent: ChartIntent, t: number): void {
    switch (intent.kind) {
      case 'toggle':
        toggleLedger();
        return;
      case 'close':
        toggleLedger(false);
        return;
      case 'leaf':
        leaf = intent.leaf;
        cursor = 0;
        kick();
        return;
      case 'cursor':
        cursor = intent.to;
        kick();
        return;
      case 'choose': {
        const page = pageAt(t);
        cursor = intent.row;
        say(choose(running.session, page, intent.row, t));
        // THE ONE VERB, HEARD: graphite on paper. The quietest thing in the bank, because a
        // choice that announces itself is the interface being pleased with you.
        audio.mark();
        rebuildSmoother();
        kick();
        return;
      }
      case 'return':
        running.session.returnToTrack(t);
        audio.mark();
        rebuildSmoother();
        kick();
        return;
      default:
        return;
    }
  }

  // The hold's disclosure is read ONCE, at open, from the frame the page starts on. It is a
  // log entry about what opening this save meant, so it must not re-fire as the clock runs.
  holdLine = holdLineFor(running.session.view(now()));

  /**
   * THE SOUND, ARMED. Nothing is constructed here: `arm` attaches three one-shot listeners and
   * returns, and the first of them to fire is the gesture that browsers require before any
   * audio may start. A player who never touches the page gets no context, no buffers, no thread
   * and no console error.
   *
   * THE ONE LINE, SAID ONCE PER BROWSER. There is no drawn mute control and there is not going
   * to be one - the sheet prints no affordance even for opening itself, and a speaker glyph
   * would be the first permanent mark on it. So the sheet says which key quiets it, through the
   * same transient aside it answers every other player act with, and then never mentions it
   * again. It is not a tutorial and it is not a toast; it is one line in the voice the chart
   * already speaks in, and it leaves in four seconds like everything else that line says.
   */
  audio.arm(() => {
    if (audio.claimFirstTelling()) {
      say(audio.muted ? AUDIO_LINES.quiet : AUDIO_LINES.first);
      kick();
    }
  });

  // ---- controls: the clock, never the boat ------------------------------------------------
  const runBtn = $('run') as HTMLButtonElement;
  runBtn.addEventListener('click', () => {
    setRate(rate > 1 ? 1 : RUN_RATE);
    runBtn.setAttribute('aria-pressed', String(rate > 1));
    kick();
  });
  $('hour').addEventListener('click', () => { jump(HOUR_MS); kick(); });
  $('day').addEventListener('click', () => { jump(DAY_MS); kick(); });
  /**
   * THE RUN UNDER `running` CHANGED: a reset, an import, a restore, a new voyage, the switch
   * from the preview to the player's own. Everything that cached the old one is rebased. The
   * audio watcher holds its own look at the old save, so the next look establishes a fresh
   * baseline and counts going to zero make no sound; the catch watcher likewise. The foot
   * says where the ledger is again, because a new run is a new player.
   */
  function freshRun(): void {
    rebuildSmoother();
    catchSnapshot = null;
    ceremony.forget();
    lastFishingKey = '';
    audio.forget();
    lastCatch = null;
    holdLine = null;
    spoken = null;
    leaf = 'species';
    cursor = 0;
    ledgerEverOpened = false;
    recordSnapshot = {};
    weightSnapshot = 0;
    tutorialCatches = Object.values(running.session.state().catches).reduce((sum, count) => sum + count, 0);
    tutorialArrival = running.session.view(now()).fix.mooredAt ?? null;
    lastUiRefreshMs = 0;
    returnReceipt = null;
    kick();
  }
  $('reset').addEventListener('click', () => {
    // The harness clears its slot before starting afresh. Preserve its current in-memory
    // voyage first; first-time player creation deliberately makes no empty-slot backup.
    const backed = player.recovery().backup('new-voyage', now());
    if (!backed.written) { reportFatal(new Error(backed.note)); return; }
    try {
      window.localStorage.removeItem(SAVE_KEY);
      window.localStorage.removeItem(CLOCK_KEY);
    } catch {
      /* nothing to remove */
    }
    if (rate > 1) { rate = 1; runBtn.setAttribute('aria-pressed', 'false'); }
    offsetMs = 0;
    // The dev key is gone, so the run is started over: `startNew` on the player session
    // replaces the voyage in place (taking the backup the product path takes), and the
    // driver, which was never stopped, keeps ticking the same session.
    player.startNew();
    freshRun();
  });

  const devBtn = $('devtoggle') as HTMLButtonElement;
  devBtn.addEventListener('click', () => {
    const on = document.body.classList.toggle('dev');
    devBtn.setAttribute('aria-pressed', String(on));
    lastClockText = '';
    kick();
  });

  /**
   * A CONTROL THAT HANDLES ITS OWN KEY GETS TO HANDLE IT.
   *
   * Every key in this game is bound on `window`, which was fine while nothing on the page
   * could take focus. It cannot stay fine now that three buttons can: Enter on a focused
   * button would fire the button AND be read as "choose the row under the hand", and
   * `SWALLOWED_KEYS` would `preventDefault` the space bar out from under it. The dev
   * strip's four clock buttons had the same latent defect - space on a focused "run x60"
   * was already being eaten whenever the ledger happened to be open.
   */
  const activates = (t: EventTarget | null): boolean => {
    const el = t as HTMLElement | null;
    if (el === null || typeof el.tagName !== 'string') return false;
    return el.tagName === 'BUTTON' || el.tagName === 'A' || el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA';
  };

  window.addEventListener('keydown', (e) => {
    if (e.defaultPrevented || e.ctrlKey || e.altKey || e.metaKey) return;
    const field = e.target;
    if (field instanceof Element && field.matches('input, select, textarea, [contenteditable="true"]')) return;
    if ((e.key === 'Enter' || e.key === ' ') && activates(e.target)) return;
    // THE SHELL OWNS THE KEYS WHILE IT OWNS THE SCREEN. On the title and under a sheet the
    // chart is not the thing in front of the player, so its keys do nothing; game-ui.ts
    // answers Escape and Tab there on `document`, before this listener sees them.
    const st = shell.state();
    const mode = st.mode;
    if ((mode !== 'voyage' && mode !== 'reading') || sheetOpen(st)) return;
    const action = actionForKey(e.key);
    // L OPENS THE FIELD GUIDE'S COLLECTION outside ?dev (CONTRACT 1.5aa). It used to lift the
    // canvas ledger; the collection page is where that ledger's plates and counts live on paper,
    // and it is where the row that also opened the ledger stood. `open-guide` lands on that page.
    if (!dev && (e.key === 'l' || e.key === 'L')) {
      e.preventDefault();
      dispatchInput({ kind: 'open-guide' });
      return;
    }
    if (!dev && !ledgerOpen && action && ['open-chart', 'open-guide', 'skip-tip', 'replay-tip', 'move-focus'].includes(action.kind)) {
      e.preventDefault();
      dispatchInput(action);
      return;
    }
    if (e.key === 'm' || e.key === 'M') {
      // M is the harness's mask under ?dev, and the menu (spec 9.4, Open Menu) in the game.
      // From the page of type the menu slip is not on screen, so the frame remembers the
      // control on the page that had focus (or the read button) and Back goes there.
      if (dev) view.setMaskMode(!view.maskMode);
      else {
        const at = (document.activeElement as HTMLElement | null)?.id ?? '';
        shell.dispatch({ kind: 'open-sheet', id: 'pause', returnFocusId: mode === 'reading' ? (at !== '' ? at : 'readbtn') : 'ui-menu' });
      }
      return;
    }
    // T TURNS THE PLAIN-TYPE PAGE ON AND OFF. It is named on that page and by the first
    // tab stop, and nowhere on the sheet: the sheet cannot usefully print an affordance
    // for the surface a player reaches for because they cannot read the sheet.
    if (e.key === 't' || e.key === 'T') {
      setReading(!reading);
      return;
    }
    // S QUIETS HER, AND THE CHOICE OUTLIVES THE TAB. The preference is persisted by the audio
    // lane in its own versioned key, so a player who mutes and reloads stays muted. The line
    // that answers is the chart's, not a new surface.
    if (e.key === 's' || e.key === 'S') {
      // The profile is authoritative for mute once it is open (Task 4 ruling): the key writes
      // the setting, and the setting reaches the engine through `applySettingsToHost`.
      commands.updateSettings({ muted: !audio.muted });
      say(audio.muted ? AUDIO_LINES.quiet : AUDIO_LINES.loud);
      kick();
      return;
    }
    // REEL IS SPACE, WITH THE LEDGER SHUT. Never R: the ledger maps R to Return-to-natural-
    // course whenever its exit copy is drawn, and Reel must not sit on a key any surface maps
    // to a gameplay command (catch-instrument.test.ts holds the pair). While the ledger is
    // open Space chooses a row, so the instrument stands down and the chart takes it below.
    if (mode === 'voyage' && actionForKey(e.key)?.kind === 'reel-presentation' && reelKey(e.key, ledgerOpen)) {
      e.preventDefault();
      reel();
      return;
    }
    const t = now();
    const copy = chartCopyAt(t, performance.now());
    // Closed canvas controls are not player-facing, and outside ?dev no key opens the canvas
    // ledger (CONTRACT 1.5aa; L is taken above). The plain-type page keeps R, its drift key.
    if (!dev && !ledgerOpen && !reading) return;
    const intent = keyIntent(e.key, copy, pageAt(t).rows.length);
    if (intent.kind === 'none') return;
    // Swallowed only when the ledger has actually taken the key, so the dev harness keeps its
    // own focus ring and a shut chart never eats a page scroll.
    if (SWALLOWED_KEYS.includes(e.key)) e.preventDefault();
    act(intent, t);
  });

  /**
   * THE SHEET, TOUCHED. There is no printed affordance for opening it, which is the point:
   * Sable's bar carries none either, and a hint painted on the paper would be the permanent
   * chrome this whole design is arranged to avoid. Once it is open the paper is a ledger you
   * put a finger on, so a hit is resolved against the SAME layout the painter drew from.
   */
  view.canvas.addEventListener('click', (e) => {
    if (shell.state().mode !== 'voyage' || sheetOpen(shell.state()) || (!dev && !ledgerOpen)) return;
    const rect = view.canvas.getBoundingClientRect();
    const artX = ((e.clientX - rect.left) / rect.width) * artView.w - chartX();
    const artY = ((e.clientY - rect.top) / rect.height) * artView.h;
    const t = now();
    const copy = chartCopyAt(t, performance.now());
    const panelH = panelHeight(copy.lift);
    if (artY < artView.h - panelH) return;
    act(hitAt(copy, artX, artY - (artView.h - panelH)), t);
  });

  // ---- the three controls a keyboard reaches, and the one it chooses rows with -------------
  readBtn.addEventListener('click', () => setReading(!reading));
  ledgerBtn.addEventListener('click', () => toggleLedger());
  driftBtn.addEventListener('click', () => act({ kind: 'return' }, now()));
  /**
   * THE LEDGER'S ROWS AS POINTER TARGETS THAT MEET THE SIZE BAR, which the canvas cannot.
   *
   * MEASURED: the sheet pitches its rows at `LINE_H` = 11 art px, so a row spans 11 CSS px
   * at grain 1 and 22 at grain 2, and 24 CSS px needs an apparent art pixel of 24/11 =
   * 2.19 - which at dpr 1 first happens in a box 2880 CSS px wide. Every ordinary window
   * fails it, and the pitch cannot be padded out of it either: two adjacent rows 11 apart
   * cannot both be 24 tall without overlapping. So the size bar is met the way WCAG 2.5.8
   * allows it to be met - by a different control on the same page that does the same job -
   * and these rows are 2.5rem tall and grow with the reader's own text size.
   */
  sayRows.addEventListener('click', (e) => {
    const li = (e.target as HTMLElement | null)?.closest('li');
    if (li === null || li === undefined || li.parentElement !== sayRows) return;
    const row = Array.prototype.indexOf.call(sayRows.children, li);
    if (row >= 0) act({ kind: 'choose', row }, now());
  });

  if (params.has('mask')) view.setMaskMode(true);
  // `?ledger` and `?leaf=places` are screenshot parameters for the canvas ledger, which is the
  // harness's alone (CONTRACT 1.5aa): a player's address bar cannot open it.
  if (dev && params.get('leaf') === 'places') leaf = 'places';
  if (dev && params.has('ledger')) {
    ledgerOpen = true;
    ledgerEverOpened = true;
    ledgerChangedAtReal = -1e12;
    // `?ledger` opens the book without going through `toggleLedger`, deliberately - it wants the
    // sheet already up rather than lifting - so it has to ask for the plates itself. This is the
    // one boot path that fetches art, and it is the path a screenshot script takes, never a
    // player's cold load.
    askForPlates();
  }
  // `?read` opens the plain-type page, which is how it is photographed and measured. It sets
  // the flag rather than calling setReading, because that would move focus during start-up.
  window.addEventListener('pagehide', () => {
    if (rate > 1) setRate(1);
    persistOffset();
    // Leave without losing a choice made in the last fifteen seconds.
    if (voyageRunning) player.save();
  });

  // ---- the paper shell -----------------------------------------------------------------------
  const seams: HostSeams = {
    dev,
    saveKeys: dev ? [SAVE_KEY] : SAVE_KEYS,
    now,
    storage: browserSaveStorage,
    player: () => player,
    voyageRunning: () => voyageRunning,
    startReal(): void {
      const before = returnSnapshot(player.voyage().state(), profileFromEnvelope(player.voyage().envelope(), now()), player.voyage().view(now()));
      running.stop();
      running = startPlayerVoyage(player, { clock: now });
      voyageRunning = true;
      freshRun();
      returnReceipt = offlineReturn(before, returnSnapshot(running.session.state(), player.profile(), running.session.view(now())));
      if (returnReceipt) player.setTutorial(advanceTutorial(player.profile().tutorial, { kind: 'away' }));
      applySettingsToHost();
    },
    stopReal(): void {
      if (!voyageRunning) return;
      running.stop();
      voyageRunning = false;
      running = preview();
      freshRun();
    },
    freshRun,
    applySettings: applySettingsToHost,
    presentation: () => (voyageRunning
      ? buildPresentation(now(), running.session.state(), running.session.view(now()), player.profile(), ceremony.ephemeral())
      : null),
    ledgerOpen: () => ledgerOpen,
    offlineReturn: () => returnReceipt,
    dismissOfflineReturn: () => { returnReceipt = null; },
    lastInput: () => lastInput,
    reel,
    reload: () => { window.location.reload(); },
  };
  /**
   * THE PROFILE'S SETTINGS, APPLIED TO THE PAGE. Mute and master level go to the engine (the
   * engine's own key is the pre-profile fallback and is written too); scale, text size,
   * motion and contrast are written onto the root by game-ui.ts itself. Reading mode is a
   * shell mode, applied when the voyage is the thing on screen (the listener below).
   *
   * The operating system's reduced-motion preference is never copied into the profile (fix
   * round 2): the stylesheet honours it on its own, live, for every save, and the profile's
   * checkbox only ever adds to it. Seeding it would have made a system setting look like the
   * player's choice and outlive the system's change of mind.
   */
  function applySettingsToHost(): void {
    const s = player.profile().settings;
    // The engine handles visibility separately; background silence must not become a saved preference.
    audio.setMix({ master: s.masterVolume, sea: s.seaVolume, effects: s.effectsVolume, muted: s.muted });
    void view.setBoatFittings(currentBoatFittings(player.profile().outfitter)).catch(reportFatal);
    lastKey = '';
  }
  const commands = buildCommands(seams);
  const shell = mountGameUi($('game-ui'), commands);
  ui = shell;
  function dispatchInput(action: PadAction): void {
    const st = shell.state();
    const surface = [...shell.root.querySelectorAll<HTMLElement>('[role="dialog"]:not([hidden])')].find(el => !el.closest('[inert]')) ?? shell.root;
    if (action.kind === 'move-focus') { moveFocus(surface, action.delta); return; }
    if (action.kind === 'adjust-value') { adjustFocusedValue(document, action.delta); return; }
    if (action.kind === 'confirm') {
      const focused = document.activeElement as HTMLElement | null;
      if (focused && (shell.root.contains(focused) || (reading && host.contains(focused))) && focused.matches('button, input[type="checkbox"], input[type="radio"], a[href], summary')) focused.click();
      else { moveFocus(surface, 1); }
      return;
    }
    if (action.kind === 'back') {
      if (ledgerOpen) toggleLedger(false);
      else document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true }));
      return;
    }
    if (st.mode === 'title') {
      if (action.kind === 'open-guide') document.getElementById('title-help')?.click();
      if (action.kind === 'open-menu') shell.dispatch({ kind: 'open-sheet', id: 'settings', returnFocusId: 'title-settings' });
      return;
    }
    if (sheetOpen(st) || st.mode === 'fatal-recovery') return;
    if (action.kind === 'open-chart' || action.kind === 'open-guide' || action.kind === 'open-menu') {
      toggleLedger(false);
      const id = action.kind === 'open-chart' ? 'chart' : action.kind === 'open-guide' ? 'guide' : 'pause';
      document.getElementById(id === 'pause' ? 'ui-menu' : `ui-${id}`)?.click();
    } else if (action.kind === 'reel-presentation') reel();
    else if (action.kind === 'skip-tip') (shell.root.querySelector<HTMLElement>(action.all ? '#tip-skip-all' : '#tip-skip') ?? shell.root.querySelector<HTMLElement>('[data-skip-tip]'))?.click();
    else if (action.kind === 'replay-tip') { commands.setTutorial(replayTutorial(action.stepId)); shell.refresh(); }
  }
  const padAction = (action: PadAction) => { lastInput = 'gamepad' as const; dispatchInput(action); shell.refresh(); };
  let stopPadInput = mountPadInput(padAction);
  window.addEventListener('pagehide', () => stopPadInput());
  window.addEventListener('pageshow', event => { if (event.persisted) stopPadInput = mountPadInput(padAction); });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (voyageRunning) {
        hiddenSnapshot = returnSnapshot(running.session.state(), player.profile(), running.session.view(now()));
        player.save();
      }
      if (timeoutHandle) window.clearTimeout(timeoutHandle);
      timeoutHandle = 0;
    } else if (voyageRunning && hiddenSnapshot) {
      player.tick(now());
      returnReceipt = offlineReturn(hiddenSnapshot, returnSnapshot(running.session.state(), player.profile(), running.session.view(now())));
      hiddenSnapshot = null;
      if (returnReceipt) {
        player.setTutorial(advanceTutorial(player.profile().tutorial, { kind: 'away' }));
        if (!sheetOpen(shell.state())) shell.dispatch({ kind: 'open-sheet', id: 'offline', returnFocusId: 'ui-menu' });
      }
      ceremony.forget();
    }
    applySettingsToHost();
    if (!document.hidden) kick();
  });
  window.addEventListener('reelin-before-close', (event) => {
    const result = voyageRunning ? player.save() : { written: true, note: '' };
    Object.assign((event as CustomEvent).detail ?? {}, result);
    if (!result.written) shell.showFatal('This voyage could not be saved. Export your save before closing.');
  });
  const reach = $('reach');
  shell.subscribe((s, prev) => {
    const onVoyage = s.origin === 'voyage' && s.mode !== 'fatal-recovery';
    // Fishing line and catch marks belong to the voyage; the detailed chart's own
    // texture is present only while opened. The title sits over the bare sea.
    view.setGuiVisible(onVoyage);
    reach.hidden = !((s.mode === 'voyage' || s.mode === 'reading') && !sheetOpen(s));
    if (s.mode === 'reading') applyReading(true, prev.mode !== 'boot' && !bootReading);
    else if (s.mode !== 'sheet') applyReading(false);
    // The persisted preference: a voyage that comes on screen opens as a page of type.
    if (s.mode === 'voyage' && prev.mode !== 'voyage' && prev.mode !== 'reading' && player.profile().settings.readingMode) {
      shell.dispatch({ kind: 'read', on: true });
      return;
    }
    kick();
  });
  $('toosmall-save').addEventListener('click', () => shell.dispatch({ kind: 'open-sheet', id: 'save', returnFocusId: 'toosmall-save' }));
  applySettingsToHost();
  // `?read` opens the plain-type page, which is how it is photographed and measured. It must
  // not move focus during start-up, so the listener is told this is the boot path.
  let bootReading = params.has('read');
  shell.dispatch({ kind: 'booted' });
  if (dev) shell.dispatch({ kind: 'start-voyage' });
  if (bootReading && dev) shell.dispatch({ kind: 'read', on: true });
  bootReading = false;

  // ---- local proof surface: observations and explicit test controls -------------------------
  const startedReal = performance.now();
  (window as unknown as { reelinWorld: unknown }).reelinWorld = {
    now,
    /** The shell's state: which surface is in front of the player and which sheets are open. */
    shell: () => shell.state(),
    /** The mounted shell, for instruments that drive sheets: `ui.dispatch`, `ui.state`. */
    ui: shell,
    /** The player's session: profile, settings, recovery. */
    player: () => player,
    freeze(at?: number | null): number | null {
      frozenAt = at === null ? null : (at ?? now());
      kick();
      return frozenAt;
    },
    setMask(on: boolean): void {
      view.setMaskMode(on);
    },
    setLedger(on: boolean): void {
      // An instrument control, reached by a script and by no player control.
      toggleLedger(on, true);
    },
    /** The plain-type page, for a script that wants to measure it or photograph it. */
    setReading(on: boolean): void {
      setReading(on);
    },
    /** Exactly what the page is saying, so a script can hold it against what was painted. */
    speech: () => chartSpeech(chartCopyAt(now(), performance.now()), { ledger: dev }),
    /**
     * THE ONLY WORK THE PLAIN-TYPE PAGE DOES PER STEP, TIMED - and timed here rather than
     * inferred from whole-page busy time, for the reason the audio lane wrote beside its
     * own probe: the renderer's per-step cost moves by more than the thing being measured,
     * so a before-and-after on `busyMs` measures the boat's speed and the water's frame.
     *
     * This is the honest attribution. It composes the speech and writes it into the page,
     * against the CURRENT sheet, so the string comparisons in `render` all come out equal -
     * which is what they do on every step where the sheet has not changed a word, and is
     * the steady state rather than the moment something happens.
     */
    speechProbe: (n = 2000) => {
      const copy = chartCopyAt(now(), performance.now());
      const speech = chartSpeech(copy, { ledger: dev });
      readout.render(speech, reading);
      const t0 = performance.now();
      for (let i = 0; i < n; i++) readout.render(chartSpeech(copy, { ledger: dev }), reading);
      const ms = performance.now() - t0;
      return { calls: n, totalMs: +ms.toFixed(3), usPerCall: +((ms * 1000) / n).toFixed(3) };
    },
    /**
     * WHAT THE ARRIVAL CAMERA COSTS PER STEP, TIMED, and timed here for the reason the speech
     * probe above and the audio probe below both give: whole-page `busyMs` moves by more than
     * the thing being measured. MEASURED on this box, drifting in open water with eight islands
     * on screen, `?look=0` against `?look=1` over 25 s each: 1.559 ms against 1.672 ms a step at
     * 0.621 and 0.705 draws a step. Per DRAW that is 2.51 ms against 2.37 - the wrong way round.
     * The camera is under the noise floor of a loud machine and a before-and-after on the page
     * total cannot see it, so this times the camera itself and nothing else.
     *
     * The step calls `cameraAt` once and `cameraPxPerRealMs` twice, so multiply by three.
     */
    cameraProbe: (n = 20000) => {
      const at = now();
      const g = smoothedAt(at);
      const t0 = performance.now();
      for (let i = 0; i < n; i++) cameraAt(g, at);
      const ms = performance.now() - t0;
      const cam = cameraAt(g, at);
      return { calls: n, totalMs: +ms.toFixed(3), usPerCall: +((ms * 1000) / n).toFixed(4), shiftPx: cam.shiftPx, panPx: cam.panPx, hold: +g.hold.toFixed(3) };
    },
    /** Hide the chart for a delivered screenshot that must match `capture()` pixel for pixel. */
    gui(on: boolean): void {
      view.setGuiVisible(on);
    },
    capture: () => view.capture(),
    frame: () => computeFrame(now()),
    chart: () => chartCopyAt(now(), performance.now()),
    /**
     * WHAT EVERY CHARTED PLACE WEARS, AND WHETHER THE DEAL SAW IT. Raw facts only - this
     * publishes no rule, so it cannot agree with a broken one. The judging is in
     * `tools/lanes/platform-trust/delivered.test.ts`, which reads this off a real page.
     *
     * WHY IT EXISTS. The deal above is made ONCE OVER THE WHOLE CHART, and that property is
     * load-bearing: `computeFrame` culls the place list to the camera, so an assignment that
     * saw only the culled list would hand a place a different island as the camera moved.
     * `src/render/islets.test.ts` fences `assignPlaceSprites` by calling it with a list the
     * TEST builds, so it proves the function and cannot see this call. MEASURED at 563dda5:
     * culling the argument here to `chart.places.filter((_p, i) => i % 2 === 0)` type-checks
     * clean and passes every test in the suite. This closes that.
     *
     * `charted` is read from `running.session.world().chart` HERE rather than from any local,
     * so a cull applied to the deal cannot also narrow what it is being compared against.
     * `worn` is `spriteForPlace` over the whole chart, which is what the renderer asks per
     * visible place - so it also catches an assignment that never reaches the renderer at all,
     * not only one that was dealt short.
     *
     * THE OTHER TWO WIRINGS ON THIS LINE OF THE HOST, both the same shape and both found by
     * sweeping for it. `pool` and `field` are `harbourSpriteIds` and `fieldSpriteIds` over the
     * SAME argument, both return `string[]`, so swapping them type-checks clean and puts the
     * scenery on the named places; `flagged` is the manifest declaration those two are supposed
     * to partition, published raw so the test can say which got which. `drawn` is the authored
     * table as the manifest states it, before the host re-keys it into a map at line 327 -
     * writing `[s.id, s.place]` there is still `Map<string, string>` and silently loses every
     * drawn island. Neither had a fence; `src/render/islets.test.ts` builds both by hand.
     *
     * Off the step loop entirely: nothing here is called by `step` or `paint`.
     */
    places: () => {
      const charted = running.session.world().chart.places;
      return {
        charted: charted.map((p) => p.id),
        dealt: [...(isletSources.assignment?.keys() ?? [])],
        pool: [...isletSources.harbour],
        field: [...fieldSprites],
        flagged: view.manifest.islets.filter((s) => s.harbour === true).map((s) => s.id),
        drawn: view.authored.islands.map((s) => ({ place: s.place, sprite: s.id })),
        authored: charted.filter((p) => isletSources.authored.has(p.id)).map((p) => p.id),
        worn: charted.map((p) => spriteForPlace(p, isletSources) ?? null),
      };
    },
    /**
     * THE THREE WIRINGS IN THIS FILE THAT NO TEST CAN REACH, PUBLISHED AS RAW FACTS.
     *
     * -- THE HOLE, WHICH IS A CLASS AND HAS NOW RECURRED TWICE ---------------------
     *
     * This file HAS NO EXPORT STATEMENT. Nothing in it can be imported, so every call site in
     * it is unfenced by construction, and the two rounds before this one each shipped a
     * defect of exactly that shape. The place deal was the first; `places()` below closed it.
     * The camera was the second, and it was worse, because the plant was invisible to every
     * instrument the run owns. MEASURED at 2ce4e58, dropping `...arrivalAt(f)` from the boot
     * smoother - which silently hands a fresh page load the camera this renderer had before
     * arrivals existed:
     *
     *   tsc                                    exit 0
     *   224 render and app tests               pass
     *   tools/lanes/surfaces/occlusion.mjs     identical planted and clean
     *   tools/lanes/surfaces/arrival.mjs       identical planted and clean
     *   delivered.test.ts                      16 of 16
     *
     * Both instruments compute the camera from `calls.ts` directly and never drive the host,
     * so neither can see the host reading it wrong. `Fix.hold` is now REQUIRED rather than
     * optional, which makes that exact plant a compile error - but a required field only
     * closes the plant, not the class: `{ ...arrivalAt(f), hold: 0 }` still type-checks. So
     * the class is closed here instead, the way the sprite deal was: publish the facts, put
     * every judgement in `tools/lanes/platform-trust/wired.test.ts`, and read them off a real
     * page. Nothing here compares anything, so the page cannot agree with a broken rule.
     *
     * -- WHAT IS RAW AND WHAT WOULD HAVE BEEN A RULE -------------------------------
     *
     * `plateSpecies` is found by IDENTITY - which of the loaded bitmaps is the object the
     * sheet is about to paint - rather than by re-deriving the choice from the cursor. A
     * derivation would be the same rule `hudCopy` uses, so it would agree with it however
     * wrong it was. `handSpecies` is the id under the hand, published separately, and the
     * assertion that the two are the same lives in the test.
     *
     * `paintedInBox` is the strongest of these because it is not about a data structure at
     * all: it paints the sheet the host would paint and counts INK and WASH inside the plate
     * box. A plate that is chosen, keyed and handed to the painter and still draws nothing -
     * clipped, mis-offset, or dropped by the `plated()` gate - comes out zero here.
     *
     * Off the step loop entirely: nothing here is called by `step` or `paint`.
     */
    wiring: () => {
      const t = now();
      const g = smoothedAt(t);
      const cam = cameraAt(g, t);
      const fix = running.session.view(t).fix;
      const copy = chartCopyAt(t, performance.now());
      const page = pageAt(t);
      const hud = hudAt(t, copy);
      const drawn = copy.plate ?? null;
      let plateSpecies: string | null = null;
      if (drawn !== null) {
        for (const [id, bmp] of plates) if (bmp === drawn) plateSpecies = id;
      }
      // TWO COUNTS, BOTH RAW, BECAUSE THE BOX IS ONLY FREE PAPER ONCE THE COLUMNS TIGHTEN.
      // `paintedInBox` is every INK or WASH pixel inside the box, which on the PLACES leaf is
      // the second column's own writing at its full 372-wide position - measured, 24 art px.
      // `plateInkPx` is the same sheet painted with the plate removed and subtracted, so it is
      // what the plate itself contributed and nothing else. Neither states a rule.
      const sheet = paintChart(copy);
      const bare = paintChart({ ...copy, plate: null });
      let paintedInBox = 0;
      let plateInkPx = 0;
      for (let y = PLATE_BOX.y0; y < PLATE_BOX.y1; y++) {
        for (let x = PLATE_BOX.x0; x < PLATE_BOX.x1; x++) {
          const i = (y + CAST_PAD) * sheet.w + x;
          const v = sheet.px[i] ?? 0;
          if (v === INK || v === WASH) paintedInBox++;
          if (v !== (bare.px[i] ?? 0)) plateInkPx++;
        }
      }
      return {
        // The camera, exactly as the frame computed it.
        hold: g.hold,
        fromHold: g.fromHold,
        toHold: g.toHold,
        fromPlace: g.fromPlace,
        toPlace: g.toPlace,
        shiftPx: cam.shiftPx,
        // The pan (CONTRACT 1.5q(b)): the offset taken, the budget it is within, the tick it was
        // read on, and the two mooring epochs the endpoints carry. Facts, no rule.
        panPx: cam.panPx,
        panBudgetPx,
        tickMs: waterTickAt(t, waveMs),
        fromMoorEpochMs: g.fromMoorEpochMs,
        toMoorEpochMs: g.toMoorEpochMs,
        boatPx: cam.boatPx,
        camPx: cam.camPx,
        // The same instant as the save reports it, untouched by the smoother.
        mooredAt: fix.mooredAt ?? null,
        trackDistance: fix.trackDistance,
        // The plate.
        plateSpecies,
        handSpecies: copy.cursor >= 0 ? (page.ids[copy.cursor] ?? null) : null,
        plateW: drawn === null ? 0 : drawn.w,
        plateH: drawn === null ? 0 : drawn.h,
        platesLoaded: plates.size,
        platesReady: platesReady(plates),
        paintedInBox,
        plateInkPx,
        boxW: PLATE_BOX.x1 - PLATE_BOX.x0,
        boxH: PLATE_BOX.y1 - PLATE_BOX.y0,
        // The repaint gate, which is otherwise a template string local to this function.
        chartKey: `${dev || ledgerOpen}:${chartKey(copy, hud.layout, hud.hours)}`,
        lastChartKey,
        leaf,
        cursor: copy.cursor,
        lift: copy.lift,
      };
    },
    /** The mastery behind the rows on show, and the key the rules contribute, so a
     *  script can measure how often the sheet can be woken by an hour being spent. */
    hours: () => {
      const t = now();
      const copy = chartCopyAt(t, performance.now());
      const h = hudAt(t, copy);
      return { hours: h.hours, key: chartKey(copy, h.layout, h.hours) };
    },
    stats: () => ({ ...view.stats(), steps, busyMs: +busyMs.toFixed(2), uptimeMs: Math.round(performance.now() - startedReal), rate, offsetMs: Math.round(offsetMs), frozen: frozenAt !== null, ledgerOpen, reading, leaf, cursor, target: running.session.state().targetSpecies, order: running.session.order().kind, waveMs, field }),
    /**
     * THE SOUND, MEASURABLE FROM A SCRIPT. The author of the audio lane cannot hear, so the
     * only honest evidence is a number, and this is where a browser gets one: `audioStats`
     * publishes the node count and the one-time render cost, `audioPlay` fires a named moment
     * without waiting for a fish, and `audioCapture` renders the identical graph through an
     * OfflineAudioContext and hands back the samples for a spectrum and a seam check.
     */
    audioStats: () => audio.stats(),
    audioMute: (on: boolean) => audio.setMuted(on),
    /** The catch ceremony as the water and the plate read it: the model, its facts, Reel's instant. */
    catch: () => (voyageRunning ? { model: catchModelAt(now(), ceremony.ephemeral(), player.profile().settings), ...ceremony.ephemeral() } : null),
    /** Reel, exactly as the Space key or the plate's row does it. Presentation only. */
    reel,
    audioPlay: (kind: string, weightFraction = 0.5) => {
      if (kind === 'lift' || kind === 'settle') audio.sheet(kind === 'lift');
      else if (kind === 'mark') audio.mark();
      else if (kind === 'land') audio.play({ kind: 'land', species: 'cod', weightFraction, token: `probe:${Math.random()}` });
      else if (kind === 'record') audio.play({ kind: 'record', species: 'cod', token: `probe:${Math.random()}` });
      else if (kind === 'junk') audio.play({ kind: 'junk', token: `probe:${Math.random()}` });
    },
    audioCapture: captureOffline,
    /** The same analysers the tests use, so a browser measurement and a node one are comparable. */
    audioMeasure: { signal: measureSignal, joinAt: measureJoinAt, bands: bandLabels },
    /**
     * THE ONLY MAIN-THREAD WORK THIS LANE DOES PER STEP, TIMED.
     *
     * `observe` is called once a step and is the whole of the audio lane's cost on the thread
     * the game draws on; everything else it does happens on the audio render thread or once at
     * unlock. Timing it against the CURRENT state means the diff comes out empty, which is what
     * it does on the overwhelming majority of steps, so this measures the steady state rather
     * than the moment something happens. It exists because comparing whole-page busy time with
     * audio on and off was too noisy to attribute anything to: the renderer's own per-step cost
     * moves by more than the thing being measured.
     */
    audioProbe: (n = 5000) => {
      const s = running.session.state();
      audio.observe(s);
      const t0 = performance.now();
      for (let i = 0; i < n; i++) audio.observe(s);
      const ms = performance.now() - t0;
      return { calls: n, totalMs: +ms.toFixed(3), usPerCall: +((ms * 1000) / n).toFixed(3) };
    },
    session: () => running.session,
    /** The box, the scale it was fitted to, and whether the game is drawing. For scripts. */
    fit: () => ({ ...fit, boxCssW: host.clientWidth, boxCssH: host.clientHeight, minBoxCssW: pinnedView?.w ?? MIN_VIEW_W, minBoxCssH: pinnedView?.h ?? MIN_VIEW_H, pinned: pinnedView, cameraView: artView, uiFromCanvas: { ...uiFromCanvas }, measures }),
  };

  // ---- the picture follows the window ---------------------------------------------------------
  /**
   * A WINDOW THAT CHANGES SIZE IS THE NORMAL CASE, not an edge one, and it is answered by two
   * event sources and no polling.
   *
   * `ResizeObserver` on #sea covers everything that changes the box in CSS pixels: dragging the
   * frame, maximising, rotating a phone, opening devtools, the dev strip appearing. It fires on
   * layout and costs nothing when nothing moves, which is the difference between this and
   * measuring the element every frame - and idle cost is a bar on this lane.
   *
   * `matchMedia('(resolution: Ndppx)')` covers the one case the observer cannot see: DRAGGING
   * THE WINDOW TO A MONITOR WITH A DIFFERENT DEVICE PIXEL RATIO. If both displays are at the
   * same OS scaling the CSS box does not change by a pixel, so no resize is observed, but the
   * number of device pixels behind it just doubled or halved. Without this the game would keep
   * a 1x backing store on a 2x display - a soft picture that no resize would ever correct. The
   * query is rebuilt on every change because it is written against the ratio in force.
   */
  const boxNote = $('havebox');
  /** The canvas is on screen and the loop is running. Not the same as `fit.playable` until applied. */
  let drawing = false;
  let appliedKey = '';
  /**
   * HOW MANY TIMES THE ELEMENT HAS BEEN MEASURED, published so the idle-cost claim is checkable
   * from a script rather than taken on trust. Read `fit().measures`, wait, read it again: if the
   * window has not moved the two figures are equal, and no layout read happened in the loop.
   */
  let measures = 0;

  /**
   * PUT THE CANVAS ON A WHOLE DEVICE PIXEL, because a whole-number SCALE is not enough on its own.
   * The measurement (a 1600x900 box at dpr 1.25: 1,043 of 518,400 blocks slipped a row when centred
   * on device y 22.5, and a near-miss correction was no better) now lives with `wholeDevicePx` in
   * src/render/fill.ts, and `fitFill` returns `left` and `top` already on the device grid. In the
   * fill mode the band is under one art pixel, so both are almost always 0.
   */
  function placeCanvas(box: FillBox, f: FillFit): void {
    const s = view.canvas.style;
    s.left = `${f.left}px`;
    s.top = `${f.top}px`;
    if (f.mode === 'pinned') {
      // PINNED: the paper shell sits exactly over the picture, as it always did.
      placeUi(host.offsetLeft + f.left, host.offsetTop + f.top, f.cssW, f.cssH);
    } else {
      // FILLED: the paper shell fills the SCREEN inside the safe area, so every sheet anchored to an
      // edge is anchored to a real edge the player can reach, and nothing sits under a status bar
      // or the home indicator. The picture runs under the insets; the paper does not.
      const i = f.insets;
      placeUi(host.offsetLeft + i.left, host.offsetTop + i.top, Math.max(0, box.cssW - i.left - i.right), Math.max(0, box.cssH - i.top - i.bottom));
    }
    placeGrain(f);
  }
  const uiRoot = $('game-ui');
  function placeUi(left: number, top: number, w: number, h: number): void {
    const s = uiRoot.style;
    s.left = `${left}px`;
    s.top = `${top}px`;
    s.width = `${w}px`;
    s.height = `${h}px`;
    uiBox = { left, top, w, h };
  }
  /** The canvas ledger's left column on the view: centred when the view is wider than its 960. */
  function chartX(): number {
    return Math.floor((artView.w - ART_W) / 2);
  }
  /**
   * THE PAPER FOLLOWS THE PICTURE'S GRAIN. `--reelin-pixel` is the apparent art pixel in
   * whole CSS px (1 at 960x540, 2 at 1920x1080 on a 1x display; the floor is 1 so a
   * fractional grain never shrinks the paper under its authoring size), and game.css derives
   * every footprint and type size from it. Under the size floor the notice has the box and
   * the recovery sheet is drawn at 1.
   *
   * `--reelin-art-left` and `--reelin-art-drop` (CONTRACT 1.5ac) are the two CSS lengths between
   * the UI root and the canvas: the canvas's left edge from the root's left, and how far the
   * canvas's bottom edge hangs below the root's. Both 0 when pinned. Only the catch plate reads
   * them, because it is the one piece of paper placed in ART px against the moving hull.
   */
  function placeGrain(f: FillFit | null): void {
    const pixel = f === null ? 1 : Math.max(1, Math.floor(f.apparent));
    const root = document.documentElement.style;
    root.setProperty('--reelin-pixel', String(pixel));
    // The picture's own scale, unfloored: only the catch plate reads it, to stand on the strip
    // the canvas actually draws (game.css).
    root.setProperty('--reelin-art', String(f === null ? 1 : f.apparent));
    const canvasLeft = f === null ? uiBox.left : host.offsetLeft + f.left;
    const canvasTop = f === null ? uiBox.top : host.offsetTop + f.top;
    uiFromCanvas.x = uiBox.left - canvasLeft;
    uiFromCanvas.y = uiBox.top - canvasTop;
    const drop = f === null ? 0 : canvasTop + f.cssH - (uiBox.top + uiBox.h);
    root.setProperty('--reelin-art-left', `${-uiFromCanvas.x}px`);
    root.setProperty('--reelin-art-drop', `${drop}px`);
  }

  function applyFit(): void {
    measures++;
    const box = measureBox();
    const next = fitFill(box);
    // Kept current while the notice is up, so the figure counts towards the size that works
    // as the frame is dragged rather than reporting the size the page happened to open at.
    if (!next.playable) boxNote.textContent = `${Math.round(box.cssW)} by ${Math.round(box.cssH)}`;
    // THE VIEW IS IN THE KEY (CONTRACT 1.5ac): a window that changes shape at the same grain now
    // changes how many art pixels are drawn, and that has to reach the renderer and the camera.
    const key = `${next.mode}:${next.grain}:${next.dpr}:${next.view.w}x${next.view.h}:${next.playable ? 'y' : 'n'}`;
    fit = next;
    if (key !== appliedKey) {
      appliedKey = key;
      document.body.classList.toggle('toosmall', !next.playable);
      // Set HERE and not in a stylesheet, because world-view.ts sets `display: block` on the
      // element itself and an inline style beats any rule in world.html. MEASURED: with the
      // rule alone the phone kept a 320x180 canvas on screen under the notice. It is one
      // function now, because the plain-type page is a second reason to hide the canvas and
      // a resize while that page was up used to put the picture back underneath it.
      showCanvas();
      if (next.playable) {
        // The camera first, so the redraw `setGrain` makes of the last frame and the step that
        // follows both frame the new view. Resize and redraw happen in this one task, inside the
        // ResizeObserver callback, before the browser paints: no frame is shown between a cleared
        // backing store and the redrawn one, so a rotation does not flash.
        setCameraView(drawableView(next));
        view.setGrain(next.grain, next.dpr, drawableView(next), { w: next.canvasDeviceW, h: next.canvasDeviceH });
      }
    }
    if (!next.playable) {
      drawing = false;
      // Under the floor the notice has the box, and the shell covers the notice's box so the
      // save sheet the notice offers has somewhere to be.
      placeUi(host.offsetLeft, host.offsetTop, box.cssW, box.cssH);
      placeGrain(null);
      return;
    }
    // Every time, not only when the grain changed: the free space either side moves with the
    // window while the scale stands still, and it is the free space that decides the offset.
    placeCanvas(box, next);
    if (!drawing) {
      drawing = true;
      kick();
    } else if (lastKey === '') {
      // The view changed under a running loop: draw the new framing now, not at the next wake.
      kick();
    }
  }

  let dprQuery: MediaQueryList | null = null;
  function onDprChange(): void {
    watchDpr();
    applyFit();
  }
  function watchDpr(): void {
    if (dprQuery) dprQuery.removeEventListener('change', onDprChange);
    dprQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
    dprQuery.addEventListener('change', onDprChange);
  }
  watchDpr();
  new ResizeObserver(applyFit).observe(host);
  applyFit();
}

main().catch(reportFatal);
